import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import axios from 'axios';
import RefundPreviewSteps, { RefundPreviewData } from '../components/common/RefundPreviewSteps';
import { adminRefundPreviewService } from '../services/digipay.service';
import JalaliDatePicker, { toPersianDigits } from '../components/common/JalaliDatePicker';
import AdminPostalReturnPanel from '../components/arbitration/AdminPostalReturnPanel';
import OrderTimeline from '../components/orders/OrderTimeline';
import SalesHistoryTab from '../components/orders/SalesHistoryTab';
// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import { API_BASE_URL } from '../config/constants';
import './EntityManagement.css';
import SnappPayConfirmDialog, { SnappPayConfirmRow } from '../components/common/SnappPayConfirmDialog';
import SnappPayOrderEditor, { EditorChange } from '../components/common/SnappPayOrderEditor';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const formatJalaliShort = (iso: string): string => {
  if (!iso) return '';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD')); }
  catch { return iso; }
};

// Enums
export enum OrderStatus {
  PENDING_PAYMENT = 'pending_payment',
  PROCESSING = 'processing',
  PARTIALLY_DELIVERED = 'partially_delivered',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  DISPUTED = 'disputed',
  REFUNDED = 'refunded'
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded'
}

export enum OrderItemStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  DISPUTED = 'disputed'
}

// Interfaces
interface OrderItem {
  _id: string;
  productId: string | { _id: string; name?: string; slug?: string };
  productTitle: string;
  quantity: number;
  originalUnitPrice?: number;
  unitPrice: number;
  discountAmount?: number;
  discountPercentage?: number;
  totalPrice: number;
  storeId: string | { _id: string; name?: string };
  storeName: string;
  accountsRequested: number;
  accountsDelivered: number;
  status: OrderItemStatus | 'refunded';
  sellerCommission: number;
  sellerNetAmount: number;
  escrowReleaseDate?: string;
  // واحدهای چندتایی (هر واحد از quantity>1 یک OrderItem جداست)
  unitNumber?: number;
  unitTotalInGroup?: number;
  // حجم/سایز انتخاب‌شده هنگام خرید
  selectedCapacity?: {
    key: string;
    name: string;
    price: number;
    originalPrice?: number;
    discountPercentage?: number;
    capacityKey?: string;
  };
  fulfillmentType?: 'account_delivery' | 'info_only' | 'postal_delivery';
  deliveryMethod?: 'credentials' | 'qr';
  qrDelivery?: {
    status: string;
    startedAt?: string;
    requestedAt?: string;
    loginCodeSentAt?: string;
    confirmedAt?: string;
    history?: Array<{ action: string; actorRole: 'buyer' | 'seller'; at: string }>;
  };
  // دیتای واردشده توسط خریدار در چک‌اوت (مثلاً توضیحات سفارش)
  buyerProvidedData?: Record<string, unknown>;
  buyerProvidedAt?: string;
  deliveredAccounts?: Array<{
    deliveredAt: string;
    deliveredByType?: 'owner' | 'employee';
    accountInfo?: { username?: string; email?: string; additionalInfo?: any };
  }>;
  infoNotices?: Array<{ message: string; sentAt: string }>;
  refundedQuantity?: number;
  refundedAmount?: number;
  postalDeliveryDetails?: {
    shippingCost?: number;
    trackingNumber?: string;
    trackingCode?: string;
    shippingAddress?: any;
    shippingStatus?: string;
    shippedAt?: string;
    deliveredAt?: string;
  };
  disputeInfo?: {
    reason: string;
    description: string;
    status: string;
    openedAt: string;
  };
  // ✅ Postal-return tracking — present after admin refunds a delivered postal item.
  // Tracks the buyer-ships-back → seller-confirms → wallet-lock-released cycle.
  postalReturnInfo?: {
    status: 'pending_buyer_ship' | 'shipped_by_buyer' | 'received_by_seller' | 'rejected_by_seller' | 'timed_out';
    refundAmount: number;
    refundLockId?: string;
    buyerShipDeadline?: string;
    buyerTrackingCode?: string;
    buyerShippedAt?: string;
    sellerReceivedAt?: string;
    sellerReceiveNotes?: string;
    rejectedAt?: string;
    rejectionReason?: string;
    createdAt?: string;
  };
}

interface Order {
  _id: string;
  orderNumber: string;
  userId: {
    _id: string;
    email: string;
    name?: string;
    phone?: string;
  };
  items: OrderItem[];
  subtotal: number;
  tax: number;
  commission: number;
  totalAmount: number;
  /** totalAmount + کارمزد پرداخت خریدار (فقط وقتی کارمزد وجود دارد) */
  payableAmount?: number;
  /** کارمزد پرداخت خریدار (خرید اقساطی دیجی‌پی) — خارج از totalAmount، snapshot درصد/زمان */
  /** پرداخت ترکیبی (نقدی + اقساطی) */
  paymentPlan?: {
    mode: 'split';
    status: 'pending' | 'partially_paid' | 'completed' | 'cancelled' | 'expired';
    bnplGateway: 'snapppay' | 'digipay';
    totalPayable: number;
    legs: Array<{ key: 'cash' | 'bnpl' | 'remainder'; gateway: string; baseAmount: number; surchargeAmount: number; amount: number; status: string; attempts?: number; paidAt?: string; refId?: string; authority?: string; maskedPan?: string; lastError?: string; refundedAmount?: number; refundedAt?: string; refundChannel?: string; refundRef?: string; refundError?: string }>;
    cashPaidAt?: string; expiresAt?: string; completedAt?: string; cancelledAt?: string; cancelReason?: string; cancelledBy?: string; reminderSentAt?: string;
    history?: Array<{ at: string; event: string; by: string; note?: string; amount?: number }>;
    refunds?: Array<{ key: string; requested: number; bnplAmount: number; bnplDone: boolean; cashAmount: number; cashDone: boolean; at: string; error?: string }>;
  };
  paymentSurcharge?: {
    gateway: string;
    instrument: string;
    ratePercent: number;
    mode: 'on_base' | 'gross_up';
    baseAmount: number;
    amount: number;
    status: 'planned' | 'charged' | 'adjusted' | 'refunded';
    appliedAt?: string;
    chargedAt?: string;
    actualInstrument?: string;
    adjustedAmount?: number;
    adjustedAt?: string;
    adjustmentReason?: string;
  };
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentDetails: {
    method: string;
    transactionId?: string;
    gatewayReference?: string;
    paidAt?: string;
    provider?: string;
    instrument?: string;
    ticketType?: number;
    trackingCode?: string;
    rrn?: string;
    maskedPan?: string;
    pspName?: string;
    paidAmount?: number;
    verifiedAt?: string;
    digipayDeliverReportedAt?: string;
    digipayDeliverError?: string;
    snapppayStatus?: string;
    snapppaySettledAt?: string;
    /** زمان آخرین اکشن اسنپ‌پی — مبنای شمارش معکوس ۳۰ ثانیه در ویرایشگر */
    snapppayLastActionAt?: string;
    snapppayCancelledAt?: string;
    snapppaySyncError?: string;
    gatewayRefundError?: string;
    zarinpalReversedAt?: string;
    zarinpalRefunds?: ZarinpalRefundRecord[];
  };
  refundedTotal?: number;
  figaPointsAwarded: number;
  createdAt: string;
  updatedAt: string;
}

interface ZarinpalRefundRecord {
  refundId: string;
  kind: 'reverse' | 'refund';
  sessionId?: string;
  authority?: string;
  amount: number;
  amountRial: number;
  method?: 'CARD' | 'PAYA';
  reason?: string;
  zpReason?: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  refundTime?: string;
  orderItemId?: string;
  createdAt: string;
  checkedAt?: string;
  error?: string;
  fallbackWalletCredited?: boolean;
}
interface ZarinpalRefundConfig {
  reverseEnabled: boolean;
  refundEnabled: boolean;
  /** تنظیم ادمین zarinpal_refund_channel: 'gateway' (پیش‌فرض) یا 'wallet' */
  channel?: 'gateway' | 'wallet';
  terminalId?: string | null;
  reverseWindowMinutes: number;
  defaultMethod: 'CARD' | 'PAYA';
  minRefundToman: number;
  reasons: string[];
}
const ZP_REFUND_STATUS_LABELS: Record<string, string> = { PENDING: 'در حال پردازش', SUCCESS: 'انجام شد', FAILED: 'ناموفق' };
const ZP_METHOD_LABELS: Record<string, string> = { CARD: 'آنی به کارت', PAYA: 'پایا (سیکل بعدی)' };
const ZP_REVERSE_WINDOW_MS = 30 * 60 * 1000;
/** سفارش با زرین‌پال پرداخت شده؟ (سفارش‌های قدیمی provider ندارند → authority ۳۶ کاراکتری با A/S) */
/** سفارش‌هایی که استردادشان مستقیم در خودِ درگاه انجام می‌شود و برگشت‌ناپذیر است (اسنپ‌پی/دیجی‌پی) */
const gatewayRefundProvider = (o?: Order | null): 'snapppay' | 'digipay' | null => {
  const p = String(o?.paymentDetails?.provider || '');
  return p === 'snapppay' || p === 'digipay' ? p : null;
};
const GATEWAY_LABEL: Record<string, string> = { snapppay: 'اسنپ‌پی', digipay: 'دیجی‌پی' };

const isZarinpalOrder = (o?: Order | null): boolean => {
  const pd = o?.paymentDetails;
  if (!pd) return false;
  if (pd.provider) return pd.provider === 'zarinpal';
  if (/zarinpal/i.test(pd.method || '')) return true;
  const ref = pd.gatewayReference || '';
  return ref.length === 36 && /^[AS]/.test(ref);
};
const zpReverseRemainingMs = (o?: Order | null): number => {
  const d = o?.paymentDetails?.verifiedAt || o?.paymentDetails?.paidAt;
  if (!d) return 0;
  return Math.max(0, new Date(d).getTime() + ZP_REVERSE_WINDOW_MS - Date.now());
};

const PROVIDER_LABELS: Record<string, string> = {
  zarinpal: 'زرین‌پال',
  payping: 'پی‌پینگ',
  digipay: 'دیجی‌پی',
  snapppay: 'اسنپ‌پی',
  wallet: 'کیف پول',
  mock: 'تستی',
};
const INSTRUMENT_LABELS: Record<string, string> = {
  credit: 'خرید اقساطی',
  ipg: 'کارت بانکی',
  wallet: 'کیف پول دیجی‌پی',
  bnpl: 'خرید اقساطی اسنپ‌پی',
};
const SNAPPPAY_STATUS_LABELS: Record<string, string> = {
  PENDING: 'در انتظار تأیید',
  VERIFY: 'تأیید شده (هنوز نهایی نشده)',
  SETTLE: 'نهایی شده',
  CANCEL: 'لغو شده (مسترد)',
  REVERT: 'برگشت خورده',
  REGISTERED: 'ثبت شده (به درگاه نرفته)',
};

// Helpers
const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD HH:mm');
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('fa-IR').format(price || 0);
};

const orderStatusLabels: Record<string, string> = {
  'pending_payment': 'در انتظار پرداخت',
  'processing': 'در حال پردازش',
  'partially_delivered': 'تحویل جزئی',
  'delivered': 'تحویل شده',
  'completed': 'تکمیل شده',
  'cancelled': 'لغو شده',
  'disputed': 'اختلاف',
  'refunded': 'مرجوع شده'
};

const paymentStatusLabels: Record<string, string> = {
  'pending': 'در انتظار',
  'paid': 'پرداخت شده',
  'failed': 'ناموفق',
  'refunded': 'مرجوع شده'
};

const itemStatusLabels: Record<string, string> = {
  'pending': 'در انتظار',
  'processing': 'پردازش',
  'delivered': 'تحویل شده',
  'cancelled': 'لغو شده',
  'refunded': 'مرجوع شده',
  'disputed': 'اختلاف'
};

// Map status -> tag class
const getOrderStatusTag = (status: string): string => {
  switch (status) {
    case 'pending_payment': return 'tag-warning';
    case 'processing': return 'tag-info';
    case 'partially_delivered': return 'tag-info';
    case 'delivered': return 'tag-success';
    case 'completed': return 'tag-success';
    case 'cancelled': return 'tag-danger';
    case 'disputed': return 'tag-danger';
    case 'refunded': return 'tag-warning';
    default: return '';
  }
};

const getPaymentStatusTag = (status: string): string => {
  switch (status) {
    case 'pending': return 'tag-warning';
    case 'paid': return 'tag-success';
    case 'failed': return 'tag-danger';
    case 'refunded': return 'tag-warning';
    default: return '';
  }
};

const getItemStatusTag = (status: string): string => {
  switch (status) {
    case 'pending': return 'tag-warning';
    case 'processing': return 'tag-info';
    case 'delivered': return 'tag-success';
    case 'cancelled': return 'tag-danger';
    case 'refunded': return 'tag-warning';
    case 'disputed': return 'tag-danger';
    default: return '';
  }
};

const disputeReasonLabels: Record<string, string> = {
  'wrong_account': 'کالای اشتباه',
  'account_not_working': 'کالا معیوب است',
  'missing_accounts': 'اقلام کمتر از سفارش',
  'quality_issue': 'مشکل کیفیت',
  'other': 'سایر'
};

const fulfillmentTypeLabels: Record<string, string> = {
  'account_delivery': 'تحویل دیجیتال',
  'info_only': 'اطلاعاتی (بدون ارسال)',
  'postal_delivery': 'ارسال پستی'
};

const deliveryMethodLabels: Record<string, string> = {
  'credentials': 'اعتبارنامه ذخیره‌شده',
  'qr': 'تحویل زنده با QRCODE'
};

// فلو (از 2026-08-19): خریدار کد/QR ورودش را می‌فرستد → فروشنده آن را در حساب
// وارد می‌کند و «تحویل دادم» می‌زند (code_sent = ثبت تحویل) → تأیید اختیاری خریدار.
const qrStatusLabels: Record<string, string> = {
  'pending_qr_request': 'در انتظار ارسال کد/QR توسط خریدار',
  'qr_requested': 'در انتظار ارسال کد/QR توسط خریدار (درخواست‌شده)',
  'qr_submitted': 'در انتظار وارد کردن کد و ثبت تحویل توسط فروشنده',
  'code_sent': 'فروشنده کد را وارد کرد (تحویل ثبت شده)',
  'confirmed': 'تأیید نهایی خریدار'
};

const getQrStatusTag = (status: string): string => {
  switch (status) {
    case 'pending_qr_request':
    case 'qr_requested': return 'tag-warning';
    case 'qr_submitted': return 'tag-info';
    case 'code_sent':
    case 'confirmed': return 'tag-success';
    default: return '';
  }
};

const OrderManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Tabs: «سفارشات» (order-level) / «تاریخچه فروش» (item-level)
  const [activeTab, setActiveTab] = useState<'orders' | 'sales'>('orders');

  // State
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, completed: 0, cancelled: 0 });

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Dialogs
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Order | null>(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus>(OrderStatus.PROCESSING);
  const [statusLoading, setStatusLoading] = useState(false);

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  // «کارمزد اقساطی هم برگردد؟» — null = تنظیم درگاه (از /common/settings)؛ ادمین می‌تواند برای همین استرداد عوض کند
  const [refundIncludeSurcharge, setRefundIncludeSurcharge] = useState<boolean | null>(null);
  const [surchargeRefundDefaults, setSurchargeRefundDefaults] = useState<{ snapppay: boolean; digipay: boolean }>({ snapppay: true, digipay: true });
  useEffect(() => {
    axios.get(`${API_BASE_URL}/common/settings`).then((r) => {
      const d = r.data?.data || {};
      setSurchargeRefundDefaults({ snapppay: d.snapppay_refund_includes_surcharge !== false, digipay: d.digipay_refund_includes_surcharge !== false });
    }).catch(() => undefined);
  }, []);
  /** کارمزد اقساطیِ هنوز نزد ما برای سفارش (۰ = سوئیچ نمایش داده نمی‌شود) */
  const surchargeOutstanding = (o?: Order | null): number => {
    const s: any = o?.paymentSurcharge;
    if (!s || !(Number(s.amount) > 0)) return 0;
    if (s.status === 'charged') return Math.round(Number(s.amount));
    if (s.status === 'adjusted') return Math.max(0, Math.round(Number(s.amount)) - Math.round(Number(s.adjustedAmount) || 0));
    return 0;
  };
  const effectiveIncludeSurcharge = (o?: Order | null): boolean => {
    if (refundIncludeSurcharge !== null) return refundIncludeSurcharge;
    const gw = gatewayRefundProvider(o);
    return gw === 'digipay' ? surchargeRefundDefaults.digipay : surchargeRefundDefaults.snapppay;
  };
  // «دقیقاً چه اتفاقی می‌افتد» — پیش‌نمایش سرور برای دیالوگ بازپرداخت سفارش/آیتم (با هر تغییر مبلغ/سوئیچ کارمزد دوباره محاسبه می‌شود)
  const [refundPreview, setRefundPreview] = useState<RefundPreviewData | null>(null);
  const [refundPreviewLoading, setRefundPreviewLoading] = useState(false);
  const [refundPreviewError, setRefundPreviewError] = useState<string | null>(null);
  useEffect(() => {
    if (!refundDialogOpen || !detail?._id) { setRefundPreview(null); setRefundPreviewError(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setRefundPreviewLoading(true); setRefundPreviewError(null);
      try {
        const amt = refundAmount && parseFloat(refundAmount) > 0 ? parseFloat(refundAmount) : undefined;
        const p = await adminRefundPreviewService.order(detail._id, { amount: amt, includeSurcharge: surchargeOutstanding(detail) > 0 ? effectiveIncludeSurcharge(detail) : undefined });
        if (!cancelled) setRefundPreview(p);
      } catch (e: any) {
        if (!cancelled) setRefundPreviewError(e?.response?.data?.message || e?.message || 'خطا');
      } finally {
        if (!cancelled) setRefundPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundDialogOpen, detail?._id, refundAmount, refundIncludeSurcharge, surchargeRefundDefaults]);
  const SurchargeRefundSwitch: React.FC<{ order: Order | null }> = ({ order }) => {
    const fee = surchargeOutstanding(order);
    if (!fee || !gatewayRefundProvider(order)) return null;
    const on = effectiveIncludeSurcharge(order);
    return (
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', background: 'var(--color-warning-bg, #fff8e6)', borderRadius: 8, marginBottom: 12, fontSize: 13, lineHeight: 1.8, cursor: 'pointer' }}>
        <input type="checkbox" checked={on} onChange={(e) => setRefundIncludeSurcharge(e.target.checked)} style={{ marginTop: 5 }} />
        <span>
          <strong>کارمزد اقساطی {formatPrice(fee)} تومان هم به خریدار برگردد</strong>
          <span style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: 12 }}>
            {on
              ? (gatewayRefundProvider(order) === 'snapppay' ? 'استرداد کامل = cancel کل تراکنش (شامل کارمزد).' : 'کارمزد هم از طریق دیجی‌پی برمی‌گردد.')
              : (gatewayRefundProvider(order) === 'snapppay' ? 'خاموش: به‌جای cancel، مبلغ تراکنش به «فقط کارمزد» update می‌شود و کارمزد نزد پلتفرم می‌ماند؛ اگر اسنپ‌پی نپذیرد لغو کامل می‌شود.' : 'خاموش: فقط ارزش کالا برمی‌گردد؛ کارمزد نزد پلتفرم می‌ماند و خریدار مطلع می‌شود.')}
            {' '}پیش‌فرض از تنظیمات «پرداخت اقساطی» می‌آید.
          </span>
        </span>
      </label>
    );
  };

  const [refundItemDialogOpen, setRefundItemDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
  const [refundItemReason, setRefundItemReason] = useState('');
  const [refundItemLoading, setRefundItemLoading] = useState(false);
  const [itemRefundPreview, setItemRefundPreview] = useState<RefundPreviewData | null>(null);
  const [itemRefundPreviewLoading, setItemRefundPreviewLoading] = useState(false);
  const [itemRefundPreviewError, setItemRefundPreviewError] = useState<string | null>(null);
  useEffect(() => {
    if (!refundItemDialogOpen || !selectedItem?._id) { setItemRefundPreview(null); setItemRefundPreviewError(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setItemRefundPreviewLoading(true); setItemRefundPreviewError(null);
      try {
        const p = await adminRefundPreviewService.item(selectedItem._id, { includeSurcharge: surchargeOutstanding(detail) > 0 ? effectiveIncludeSurcharge(detail) : undefined });
        if (!cancelled) setItemRefundPreview(p);
      } catch (e: any) {
        if (!cancelled) setItemRefundPreviewError(e?.response?.data?.message || e?.message || 'خطا');
      } finally {
        if (!cancelled) setItemRefundPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundItemDialogOpen, selectedItem?._id, refundIncludeSurcharge, surchargeRefundDefaults]);

  // زرین‌پال: مقصد استرداد (کارت خریدار از طریق درگاه / کیف پول) + روش (CARD آنی / PAYA پایا)
  const [zpConfig, setZpConfig] = useState<ZarinpalRefundConfig | null>(null);
  const [refundTarget, setRefundTarget] = useState<'gateway' | 'wallet'>('wallet');
  const [refundMethod, setRefundMethod] = useState<'CARD' | 'PAYA'>('CARD');
  const [refundItemTarget, setRefundItemTarget] = useState<'gateway' | 'wallet'>('wallet');
  const [zpBusy, setZpBusy] = useState<string | null>(null);

  // 💸 تعهدهای استرداد (پرداخت خریدار از حساب فروشنده — سیاست 2026-09-08)
  interface RefundObligationRow {
    _id: string; key: string; amount: number; status: 'awaiting_funds' | 'paying' | 'paid' | 'cancelled';
    source: string; reason?: string; createdAt: string; paidAt?: string; lastError?: string; attempts?: number;
    storeId?: { _id: string; name?: string } | string; orderItemId?: string;
    poolSnapshot?: { pool: number; outstandingBefore: number; shortfall: number };
    outcome?: { channelText?: string; message?: string; channel?: string };
    forcedByAdminId?: string;
  }
  const [obligations, setObligations] = useState<RefundObligationRow[]>([]);
  const [obBusy, setObBusy] = useState<string | null>(null);
  const OB_STATUS_LABELS: Record<string, string> = { awaiting_funds: 'در انتظار تأمین وجه فروشنده', paying: 'در حال پرداخت', paid: 'پرداخت شد', cancelled: 'لغو شد' };
  const loadObligations = useCallback(async (orderId: string) => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/refund-obligations`, { params: { orderId, limit: 50 }, headers: { Authorization: `Bearer ${token}` } });
      setObligations(res.data?.data?.rows || []);
    } catch { setObligations([]); }
  }, []);
  useEffect(() => {
    if (detailOpen && detail?._id) loadObligations(detail._id); else setObligations([]);
  }, [detailOpen, detail?._id, loadObligations]);
  const obligationAction = async (id: string, action: 'force-pay' | 'cancel') => {
    if (!detail) return;
    let body: any = {};
    if (action === 'force-pay') {
      if (!window.confirm('این بازپرداخت همین حالا از منابع پلتفرم به خریدار پرداخت شود؟ فروشنده تا تسویه بدهکار می‌ماند و ورودی‌های بعدی‌اش کسر می‌شود.')) return;
    } else {
      const reason = window.prompt('دلیل لغو تعهد (به خریدار پرداختی انجام نمی‌شود):', '');
      if (reason === null) return;
      if (!reason.trim()) { enqueueSnackbar('دلیل لغو الزامی است', { variant: 'warning' }); return; }
      body = { reason: reason.trim() };
    }
    setObBusy(id + action);
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/refund-obligations/${id}/${action}`, body, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar(res.data?.message || 'انجام شد', { variant: 'success' });
      loadObligations(detail._id);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا', { variant: 'error' });
    } finally {
      setObBusy(null);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${API_BASE_URL}/admin/orders/zarinpal/config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.status === 'success') {
          setZpConfig(res.data.data);
          setRefundMethod(res.data.data?.defaultMethod === 'PAYA' ? 'PAYA' : 'CARD');
        }
      } catch { /* پیکربندی زرین‌پال در دسترس نیست → فقط کیف پول */ }
    })();
  }, []);
  /** پیش‌فرض مقصد استرداد برای یک سفارش زرین‌پال: طبق تنظیم «مقصد استرداد» (gateway) اگر درگاه فعال باشد؛ وگرنه کیف پول */
  const defaultTargetFor = (o: Order | null): 'gateway' | 'wallet' => (
    o && isZarinpalOrder(o) && zpConfig?.channel !== 'wallet' && (zpConfig?.refundEnabled || zpReverseRemainingMs(o) > 0) ? 'gateway' : 'wallet'
  );
  const openRefundDialog = (o: Order) => {
    setDetail(o);
    setRefundTarget(defaultTargetFor(o));
    setRefundIncludeSurcharge(null);
    setRefundDialogOpen(true);
  };
  const openRefundItemDialog = (o: Order, item: OrderItem) => {
    setSelectedItem(item);
    setRefundIncludeSurcharge(null);
    setRefundItemTarget(o && isZarinpalOrder(o) && zpConfig?.channel !== 'wallet' && zpConfig?.refundEnabled ? 'gateway' : 'wallet');
    setRefundItemDialogOpen(true);
  };

  const getToken = () => localStorage.getItem('accessToken');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) throw new Error('Token not found');

      let url = `${API_BASE_URL}/admin/orders?page=${page}&limit=${limit}`;

      if (statusFilter) url += `&status=${statusFilter}`;
      if (paymentFilter) url += `&paymentStatus=${paymentFilter}`;
      if (dateFrom) url += `&startDate=${dateFrom}`;
      if (dateTo) url += `&endDate=${dateTo}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.status === 'success') {
        const docs: Order[] = res.data.data.docs || [];
        setOrders(docs);
        setTotal(res.data.data.totalDocs || 0);

        // Compute stats from current page
        setStats({
          total: res.data.data.totalDocs || 0,
          pending: docs.filter(o => o.status === OrderStatus.PENDING_PAYMENT).length,
          processing: docs.filter(o => o.status === OrderStatus.PROCESSING || o.status === OrderStatus.PARTIALLY_DELIVERED).length,
          completed: docs.filter(o => o.status === OrderStatus.COMPLETED || o.status === OrderStatus.DELIVERED).length,
          cancelled: docs.filter(o => o.status === OrderStatus.CANCELLED || o.status === OrderStatus.REFUNDED).length,
        });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت سفارشات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, paymentFilter, dateFrom, dateTo, search, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openDetail = async (order: Order) => {
    setDetailLoading(true);
    setDetail(null);
    setDetailOpen(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/orders/${order._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        setDetail(res.data.data);
      } else {
        setDetail(order);
      }
    } catch {
      setDetail(order);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!detail) return;
    setStatusLoading(true);
    try {
      const token = getToken();
      const res = await axios.put(`${API_BASE_URL}/admin/orders/${detail._id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        enqueueSnackbar('وضعیت سفارش بروزرسانی شد', { variant: 'success' });
        setStatusDialogOpen(false);
        load();
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در بروزرسانی وضعیت', { variant: 'error' });
    } finally {
      setStatusLoading(false);
    }
  };

  // دیجی‌پی: بازگشت کارمزد اقساطی به کیف پول خریدار (کارمزد خارج از totalAmount است و هیچ ریفاندی آن را برنمی‌گرداند)
  const [surchargeRefundLoading, setSurchargeRefundLoading] = useState(false);
  const handleRefundSurcharge = async () => {
    if (!detail?.paymentSurcharge || !(detail.paymentSurcharge.amount > 0)) return;
    if (!window.confirm(`کارمزد ${formatPrice(detail.paymentSurcharge.amount)} تومانی این سفارش به کیف پول خریدار برگردانده شود؟`)) return;
    setSurchargeRefundLoading(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/refund-surcharge`, { reason: 'بازگشت کارمزد اقساطی توسط ادمین' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        enqueueSnackbar('کارمزد به کیف پول خریدار واریز شد', { variant: 'success' });
        load();
        setDetail(null);
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در بازگشت کارمزد', { variant: 'error' });
    } finally {
      setSurchargeRefundLoading(false);
    }
  };

  // اسنپ‌پی: استعلام وضعیت / نهایی‌سازی / لغو (استرداد کامل) / کاهش مبلغ — cancel و update برگشت‌ناپذیرند
  const [snappBusy, setSnappBusy] = useState<string | null>(null);
  /** لاگ‌های اسنپ‌پی همین سفارش (از مرکز اسنپ‌پی، بدون خروج از صفحهٔ سفارش) */
  const [snappLogs, setSnappLogs] = useState<any[] | null>(null);
  const [snappLogsLoading, setSnappLogsLoading] = useState(false);
  /**
   * دیالوگ تأیید دومرحله‌ای — فقط برای عملیات اسنپ‌پی (برگشت‌ناپذیرند).
   * سایر سفارش‌ها مسیر تأیید سادهٔ قبلی خودشان را دارند.
   */
  const [snappConfirm, setSnappConfirm] = useState<
    | null
    | {
        title: string;
        description: string;
        rows?: SnappPayConfirmRow[];
        irreversibleNote?: string;
        confirmLabel?: string;
        run: () => void;
      }
  >(null);
  // ── پرداخت ترکیبی (نقدی + اقساطی): اکشن‌های ادمین ──
  const [splitBusy, setSplitBusy] = useState<string | null>(null);
  const splitCall = async (action: 'cancel' | 'extend' | 'remind' | 'retry-cash-refund', body?: any) => {
    if (!detail) return;
    setSplitBusy(action);
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/split/${action}`, body || {}, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar(res.data?.message || 'انجام شد', { variant: res.data?.status === 'partial' ? 'warning' : 'success' });
      await openDetail(detail);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در انجام عملیات', { variant: 'error' });
    } finally {
      setSplitBusy(null);
    }
  };
  const handleSplitCancel = () => {
    if (!detail?.paymentPlan) return;
    const paidCash = detail.paymentPlan.legs.filter((l) => l.gateway === 'zarinpal' && l.status === 'paid').reduce((s, l) => s + (l.amount - (l.refundedAmount || 0)), 0);
    if (!window.confirm(`پرداخت ترکیبی این سفارش لغو شود؟${paidCash > 0 ? ` مبلغ نقدی ${formatPrice(paidCash)} تومان خودکار به کیف پول خریدار واریز می‌شود` : ''} و سفارش لغو می‌شود. این عملیات برگشت‌ناپذیر است.`)) return;
    const reason = window.prompt('دلیل لغو (برای خریدار نمایش داده می‌شود):', 'لغو پرداخت ترکیبی توسط پشتیبانی') || undefined;
    splitCall('cancel', { reason });
  };
  /**
   * واگرد کامل سفارش پرداخت‌شده — سفارش لغو می‌شود و هر بخشی که پولش هنوز دست ماست برمی‌گردد.
   * `lost` یعنی کدام بخش پولش بیرون از سیستم ما برگشته و نباید دوباره برگردد:
   *   none = کل مبلغ برگردد · bnpl = درگاه اقساطی خودش پس گرفته · cash = سهم نقدی برگشت خورده (چارج‌بک/ریورس بانک)
   */
  const handleUnwind = async (lost: 'none' | 'bnpl' | 'cash' = 'none', opts?: { force?: boolean }): Promise<void> => {
    if (!detail) return;
    const split = detail.paymentPlan?.mode === 'split';
    const gwName = detail.paymentPlan?.bnplGateway === 'digipay' || detail.paymentDetails?.provider === 'digipay' ? 'دیجی‌پی' : 'اسنپ‌پی';
    const what =
      lost === 'bnpl'
        ? (split
            ? `سهم اقساطی را خودِ ${gwName} برگردانده است؛ فقط سهم نقدی از این‌جا به کیف پول خریدار واریز می‌شود.`
            : `پول این سفارش را خودِ ${gwName} به خریدار برگردانده است؛ اینجا فقط سفارش لغو و اقلام آن بسته می‌شوند.`)
        : lost === 'cash'
          ? `سهم نقدی این سفارش برگشت خورده است؛ فقط سهم اقساطی از ${gwName} به خریدار برمی‌گردد (هیچ مبلغی به کیف پول واریز نمی‌شود).`
          : split
            ? `کل مبلغ برمی‌گردد: سهم اقساطی به اعتبار ${gwName} خریدار و سهم نقدی به کیف پول او.`
            : `کل مبلغ از طریق ${gwName} به خریدار برمی‌گردد.`;
    if (!window.confirm(`کل این سفارش لغو شود و وضعیت مالی همگام گردد؟\n\n${what}\n\nاین عملیات برگشت‌ناپذیر است.`)) return;
    const reason = window.prompt('دلیل (در سوابق سفارش ثبت می‌شود):', lost === 'cash' ? 'برگشت‌خوردن بخش نقدی' : lost === 'bnpl' ? 'برگشت پرداخت از سمت درگاه' : 'لغو کامل سفارش توسط پشتیبانی') || undefined;
    setSplitBusy('unwind');
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/unwind`, { reason, lost, force: opts?.force === true }, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar(res.data?.message || 'سفارش لغو شد', { variant: 'success' });
      await openDetail(detail);
      load();
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.data?.needsAdmin && !opts?.force) {
        if (window.confirm(`${d.message}\n\nبا این حال ادامه می‌دهید و سفارش را لغو و وجه را برمی‌گردانید؟`)) {
          setSplitBusy(null);
          return handleUnwind(lost, { force: true });
        }
      } else {
        enqueueSnackbar(d?.message || e?.message || 'خطا در لغو سفارش', { variant: 'error' });
      }
    } finally {
      setSplitBusy(null);
    }
  };

  const handleSplitExtend = () => {
    const raw = window.prompt('چند ساعت به مهلت مرحلهٔ دوم اضافه شود؟', '24');
    if (raw === null) return;
    const hours = Number(raw);
    if (!(hours > 0)) { enqueueSnackbar('عدد معتبر وارد کنید', { variant: 'warning' }); return; }
    splitCall('extend', { hours });
  };

  const snappCall = async (action: 'status' | 'settle' | 'cancel' | 'update' | 'revert', body?: any) => {
    if (!detail) return;
    setSnappBusy(action);
    try {
      const token = getToken();
      const url = `${API_BASE_URL}/admin/orders/${detail._id}/snapppay-${action}`;
      const res = action === 'status'
        ? await axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(url, body || {}, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data?.data || {};
      if (action === 'status') {
        enqueueSnackbar(`وضعیت در اسنپ‌پی: ${SNAPPPAY_STATUS_LABELS[d.status] || d.status || '—'}${d.amountToman ? ` — مبلغ ${formatPrice(d.amountToman)} تومان` : ''}`, { variant: 'info' });
      } else {
        enqueueSnackbar(res.data?.message || 'انجام شد', { variant: 'success' });
      }
      load();
      if (action !== 'status') setDetail(null);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در ارتباط با اسنپ‌پی', { variant: 'error' });
    } finally {
      setSnappBusy(null);
    }
  };
  /** نمایش مبلغ با «تومان» برای ردیف‌های دیالوگ */
  const t = (n: number) => `${formatPrice(n)} تومان`;

  /** توکن پرداخت اسنپ‌پی این سفارش (همان چیزی که همهٔ عملیات درگاه با آن انجام می‌شود) */
  const snappToken = detail?.paymentDetails?.gatewayReference || detail?.paymentDetails?.trackingCode || '';

  const copyText = (v: string) => {
    navigator.clipboard?.writeText(String(v)).then(
      () => enqueueSnackbar('کپی شد', { variant: 'success' }),
      () => enqueueSnackbar('کپی نشد', { variant: 'warning' }),
    );
  };

  /** لاگ‌های اسنپ‌پی همین سفارش — همان دادهٔ مرکز اسنپ‌پی، این‌جا بدون جابه‌جایی صفحه */
  const loadSnappLogs = async (orderId: string) => {
    setSnappLogsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/snapppay/logs`, {
        params: { orderId, limit: 25 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data;
      setSnappLogs(Array.isArray(d) ? d : (d?.logs || d?.items || []));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در دریافت لاگ‌های اسنپ‌پی', { variant: 'error' });
    } finally {
      setSnappLogsLoading(false);
    }
  };

  /** تعداد فعال یک قلم = خریداری‌شده منهای مستردشده */
  const activeQtyOf = (it: any) =>
    Math.max(0, (Number(it.quantity) || 1) - (Number(it.refundedQuantity) || 0));

  /**
   * ✏️ ثبت ویرایش اقلام سفارش اسنپ‌پی — کاهش تعداد و حذف ردیف‌ها با یک درخواست.
   * اگر همهٔ اقلام حذف شده باشند، سرور خودش سفارش را لغو می‌کند.
   */
  const handleSnappEdit = (
    changes: EditorChange[],
    summary: { refund: number; willCancel: boolean; lines: string[] },
  ) => {
    if (!detail) return;
    setSnappConfirm({
      title: summary.willCancel
        ? 'حذف همهٔ اقلام و لغو سفارش در اسنپ‌پی'
        : 'ثبت ویرایش اقلام سفارش در اسنپ‌پی',
      description: summary.willCancel
        ? 'همهٔ اقلام این سفارش حذف شده‌اند، بنابراین به‌جای کاهش مبلغ، کل سفارش نزد اسنپ‌پی لغو می‌شود و تمام مبلغ به اعتبار خریدار برمی‌گردد. سفارش در گمیزو هم «مسترد» می‌شود.'
        : 'سبد سفارش نزد اسنپ‌پی با اقلام و تعداد جدید به‌روزرسانی می‌شود و مبلغ واحدهای حذف‌شده به اعتبار خریدار برمی‌گردد. همهٔ تغییرها با یک درخواست ثبت می‌شوند.',
      rows: [
        ...summary.lines.map((l, i) => ({ label: `تغییر ${i + 1}`, value: l, danger: true })),
        { label: 'بازگشت به خریدار', value: `${formatPrice(summary.refund)} تومان`, danger: true },
      ],
      irreversibleNote: summary.willCancel
        ? 'لغو سفارش نزد اسنپ‌پی برگشت‌ناپذیر است؛ نه سفارش برمی‌گردد و نه همان تراکنش دوباره قابل استفاده است.'
        : 'کاهش مبلغ نزد اسنپ‌پی برگشت‌ناپذیر است؛ بعد از ثبت نمی‌توان تعداد را دوباره زیاد کرد.',
      confirmLabel: summary.willCancel ? 'لغو کامل سفارش' : 'ثبت ویرایش',
      run: () => snappEditCall(changes),
    });
  };

  const snappEditCall = async (changes: EditorChange[]) => {
    if (!detail) return;
    setSnappBusy('edit');
    try {
      const token = getToken();
      const res = await axios.post(
        `${API_BASE_URL}/admin/snapppay/orders/${detail._id}/edit`,
        { changes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      enqueueSnackbar(res.data?.message || 'ویرایش ثبت شد', { variant: 'success' });
      setSnappConfirm(null);
      await openDetail(detail);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در ثبت ویرایش سفارش اسنپ‌پی', { variant: 'error' });
    } finally {
      setSnappBusy(null);
    }
  };

  const handleSnappCancel = () => {
    if (!detail) return;
    const paid = detail.paymentDetails?.paidAmount || detail.totalAmount;
    setSnappConfirm({
      title: 'لغو کامل سفارش در اسنپ‌پی',
      description:
        'کل سفارش نزد اسنپ‌پی لغو می‌شود و تمام مبلغ به اعتبار خریدار برمی‌گردد. سفارش در گمیزو هم «مسترد» می‌شود و همهٔ اقلام تسویه‌نشده بسته می‌شوند. برای این سفارش استرداد داخلی به کیف پول انجام ندهید؛ پول از خود اسنپ‌پی برمی‌گردد.',
      rows: [
        { label: 'شماره سفارش', value: String(detail.orderNumber || '—') },
        { label: 'شناسهٔ تراکنش اسنپ‌پی', value: String(detail.paymentDetails?.transactionId || '—') },
        { label: 'مبلغ بازگشتی', value: t(paid), danger: true },
      ],
      irreversibleNote:
        'لغو سفارش نزد اسنپ‌پی برگشت‌ناپذیر است. پس از اجرا نه سفارش برمی‌گردد و نه امکان فعال‌سازی دوبارهٔ همان تراکنش وجود دارد.',
      confirmLabel: 'لغو کامل سفارش',
      run: () => { setSnappConfirm(null); snappCall('cancel', { reason: 'لغو سفارش توسط ادمین' }); },
    });
  };
  const handleSnappRevert = () => {
    if (!detail) return;
    setSnappConfirm({
      title: 'بازگشت وجه قبل از نهایی‌سازی (revert)',
      description:
        'این تراکنش هنوز نزد اسنپ‌پی نهایی (settle) نشده است. با revert کل مبلغ به حساب خریدار برمی‌گردد و سفارش «مسترد» می‌شود. توجه: پس از نهایی‌سازی، revert دیگر ممکن نیست و باید از «لغو» یا «کاهش مبلغ» استفاده کرد.',
      rows: [
        { label: 'شماره سفارش', value: String(detail.orderNumber || '—') },
        { label: 'مبلغ بازگشتی', value: t(detail.paymentDetails?.paidAmount || detail.totalAmount), danger: true },
      ],
      irreversibleNote: 'revert برگشت‌ناپذیر است؛ تراکنش پس از آن قابل استفادهٔ دوباره نیست.',
      confirmLabel: 'بازگشت وجه (revert)',
      run: () => { setSnappConfirm(null); snappCall('revert', { reason: 'revert توسط ادمین از بخش سفارش‌ها' }); },
    });
    return;
    snappCall('revert', { reason: 'revert توسط ادمین از بخش سفارش‌ها' });
  };
  const handleSnappUpdate = () => {
    if (!detail) return;
    const paid = detail.paymentDetails?.paidAmount || detail.totalAmount;
    const raw = window.prompt(`مبلغ جدید سفارش (تومان) — باید کمتر یا مساوی ${formatPrice(paid)} باشد. خالی = محاسبه از آیتم‌های فعال (غیر لغو/استردادشده):`, '');
    if (raw === null) return;
    const amount = raw.trim() === '' ? undefined : Number(raw.replace(/[^\d]/g, ''));
    if (amount !== undefined && !(amount > 0)) { enqueueSnackbar('مبلغ نامعتبر است', { variant: 'warning' }); return; }
    setSnappConfirm({
      title: 'کاهش مبلغ سفارش در اسنپ‌پی (update)',
      description:
        'مبلغ سفارش نزد اسنپ‌پی کاهش می‌یابد و تفاوت به اعتبار خریدار برمی‌گردد. سبد اقلام هم با همین مبلغ جدید به‌روزرسانی می‌شود. مبلغ جدید باید کمتر یا مساوی مبلغ فعلی باشد.',
      rows: [
        { label: 'مبلغ فعلی', value: t(paid) },
        {
          label: 'مبلغ جدید',
          before: formatPrice(paid),
          after: amount !== undefined ? formatPrice(amount) : 'مجموع آیتم‌های فعال',
          danger: true,
        },
        ...(amount !== undefined ? [{ label: 'بازگشت به خریدار', value: t(Math.max(0, paid - amount)), danger: true }] : []),
      ],
      irreversibleNote:
        'کاهش مبلغ نزد اسنپ‌پی برگشت‌ناپذیر است؛ نمی‌توان مبلغ را دوباره افزایش داد.',
      confirmLabel: 'کاهش مبلغ سفارش',
      run: () => { setSnappConfirm(null); snappCall('update', { amount, reason: 'کاهش مبلغ سفارش توسط ادمین' }); },
    });
  };

  const handleDigipayDeliver = async () => {
    if (!detail) return;
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/digipay-deliver`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data?.data || {};
      if (d.reported || d.skipped === 'already_reported') {
        enqueueSnackbar('تحویل خرید اعتباری به دیجی‌پی ثبت شد', { variant: 'success' });
        load();
        setDetail(null);
      } else {
        enqueueSnackbar(d.error || `ثبت انجام نشد (${d.skipped || 'نامشخص'})`, { variant: 'warning' });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در ثبت تحویل دیجی‌پی', { variant: 'error' });
    }
  };

  // زرین‌پال: ریورس فوری (≤۳۰ دقیقه، بدون کارمزد) و استعلام وضعیت استردادهای در جریان
  const handleZpReverse = async () => {
    if (!detail) return;
    const paid = detail.paymentDetails?.paidAmount || detail.totalAmount;
    if (!window.confirm(`کل مبلغ ${formatPrice(paid)} تومانی این سفارش همین حالا از طریق ریورس زرین‌پال به کارت خریدار برگردانده شود؟ (آنی، بدون کارمزد، فقط تا ۳۰ دقیقه بعد از پرداخت) سفارش «مسترد» می‌شود و این عملیات برگشت‌ناپذیر است.`)) return;
    setZpBusy('reverse');
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/zarinpal-reverse`, { reason: 'ریورس تراکنش توسط ادمین' }, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar(res.data?.message || 'ریورس انجام شد', { variant: 'success' });
      load();
      openDetail(detail);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در ریورس زرین‌پال', { variant: 'error' });
    } finally {
      setZpBusy(null);
    }
  };
  const handleZpStatus = async () => {
    if (!detail) return;
    setZpBusy('status');
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/admin/orders/${detail._id}/zarinpal-refund-status`, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data?.data || {};
      const pending = (d.refunds || []).filter((r: ZarinpalRefundRecord) => r.status === 'PENDING').length;
      enqueueSnackbar(`استعلام انجام شد — ${d.synced?.changed || 0} تغییر؛ ${pending ? `${pending} استرداد هنوز در حال پردازش` : 'استرداد در جریانی نیست'}`, { variant: d.synced?.changed ? 'success' : 'info' });
      openDetail(detail);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در استعلام زرین‌پال', { variant: 'error' });
    } finally {
      setZpBusy(null);
    }
  };

  const handleRefund = async () => {
    if (!detail) return;
    // الزام اسنپ‌پی: قبل از ارسال cancel/update به درگاه، تأییدیهٔ دوم از ادمین گرفته شود (برگشت‌ناپذیر)
    const gwR = gatewayRefundProvider(detail);
    if (gwR) {
      const amt = refundAmount && parseFloat(refundAmount) > 0 ? parseFloat(refundAmount) : (detail.paymentDetails?.paidAmount || detail.totalAmount);
      const full = !(refundAmount && parseFloat(refundAmount) > 0);
      const what = gwR === 'snapppay'
        ? (full ? 'کل سفارش در اسنپ‌پی «لغو» (cancel) می‌شود' : 'مبلغ سفارش در اسنپ‌پی با «به‌روزرسانی» (update) کاهش می‌یابد')
        : (full ? 'کل مبلغ از طریق دیجی‌پی برگشت داده می‌شود' : 'مبلغ از طریق دیجی‌پی به‌صورت جزئی برگشت داده می‌شود');
      if (!window.confirm(`${what} و ${formatPrice(amt)} تومان به ${gwR === 'snapppay' ? 'اعتبار اسنپ‌پی' : 'ابزار پرداخت'} خریدار برمی‌گردد. هیچ مبلغی به کیف پول کازماشاپ واریز نمی‌شود و این عملیات برگشت‌ناپذیر است. ادامه می‌دهید؟`)) return;
    }
    setRefundLoading(true);
    try {
      const token = getToken();
      const data: any = { reason: refundReason || 'بازپرداخت توسط ادمین' };
      if (refundAmount && parseFloat(refundAmount) > 0) data.amount = parseFloat(refundAmount);
      if (surchargeOutstanding(detail) > 0 && gwR) data.includeSurcharge = effectiveIncludeSurcharge(detail);
      // زرین‌پال: انتخاب ادمین صریح فرستاده می‌شود (gateway = فقط کارت خریدار، wallet = کیف پول)؛
      // اسنپ‌پی/دیجی‌پی همیشه از خودِ درگاه برمی‌گردند و target ندارند.
      if (isZarinpalOrder(detail)) {
        data.target = refundTarget;
        if (refundTarget === 'gateway') data.method = refundMethod;
      }

      const res = await axios.post(`${API_BASE_URL}/admin/orders/${detail._id}/refund`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        const deferred = !!(res.data.data?.deferred ?? res.data.deferred);
        const msg = deferred
          ? (res.data.message || 'بازپرداخت ثبت شد؛ پس از تأمین وجه از حساب فروشنده خودکار پرداخت می‌شود')
          : res.data.data?.gateway
          ? (res.data.message || 'استرداد از طریق درگاه ثبت شد')
          : (res.data.message || (res.data.data?.walletUpdated ? 'بازپرداخت با موفقیت انجام شد' : 'بازپرداخت انجام شد اما خطا در واریز به کیف پول'));
        enqueueSnackbar(msg, {
          variant: deferred ? 'warning' : res.data.data?.walletUpdated ? 'success' : 'warning',
          autoHideDuration: deferred ? 9000 : undefined,
        });
        setRefundDialogOpen(false);
        setRefundReason('');
        setRefundAmount('');
        load();
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در بازپرداخت', { variant: 'error' });
    } finally {
      setRefundLoading(false);
    }
  };

  const handleRefundItem = async () => {
    if (!selectedItem || !detail) return;
    const gwRI = gatewayRefundProvider(detail);
    if (gwRI) {
      const what = gwRI === 'snapppay'
        ? 'مبلغ این آیتم با سرویس «به‌روزرسانی» (update) از سفارش اسنپ‌پی کم می‌شود — و اگر آخرین آیتم فعال باشد کل سفارش «لغو» (cancel) می‌شود'
        : 'مبلغ این آیتم از طریق سرویس بازگشت وجه دیجی‌پی برگردانده می‌شود';
      if (!window.confirm(`${what} و به ${gwRI === 'snapppay' ? 'اعتبار اسنپ‌پی' : 'ابزار پرداخت'} خریدار برمی‌گردد. هیچ مبلغی به کیف پول کازماشاپ واریز نمی‌شود و این عملیات برگشت‌ناپذیر است. ادامه می‌دهید؟`)) return;
    }
    setRefundItemLoading(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API_BASE_URL}/admin/orders/items/${selectedItem._id}/refund`, {
        reason: refundItemReason || 'بازپرداخت آیتم توسط ادمین',
        ...(surchargeOutstanding(detail) > 0 && gwRI ? { includeSurcharge: effectiveIncludeSurcharge(detail) } : {}),
        ...(isZarinpalOrder(detail) ? { target: refundItemTarget, ...(refundItemTarget === 'gateway' ? { method: refundMethod } : {}) } : {})
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.status === 'success') {
        const rd = res.data.data.refundDetails;
        if (res.data.data?.deferred) {
          enqueueSnackbar(res.data.message || 'بازپرداخت ثبت شد؛ پس از تأمین وجه از حساب فروشنده خودکار به خریدار پرداخت می‌شود', { variant: 'warning', autoHideDuration: 9000 });
        } else
        enqueueSnackbar(res.data.data?.gateway
          ? (res.data.message || `آیتم بازپرداخت شد: ${formatPrice(rd.totalRefund)} تومان از طریق درگاه`)
          : `آیتم بازپرداخت شد: ${formatPrice(rd.totalRefund)} تومان`, { variant: 'success' });
        setRefundItemDialogOpen(false);
        setSelectedItem(null);
        setRefundItemReason('');
        load();
        // ✅ FIX BUG-11: refresh the open detail dialog so the refunded item shows its new
        // status and its "بازپرداخت این آیتم" button disappears (avoids a confusing 2nd click → 400).
        if (detail?._id) {
          openDetail(detail);
        }
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در بازپرداخت آیتم', { variant: 'error' });
    } finally {
      setRefundItemLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setStatusFilter('');
    setPaymentFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  const hasActiveFilters = !!(search || statusFilter || paymentFilter || dateFrom || dateTo);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت سفارشات</h1>
          <p className="entity-subtitle">مدیریت و پیگیری سفارشات کاربران</p>
        </div>
        <div className="header-actions">
          {activeTab === 'orders' && (
            <button className="header-btn refresh" onClick={load} title="بروزرسانی">
              <RefreshIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={activeTab === 'orders' ? 'filter-btn' : 'filter-btn outline'}
          onClick={() => setActiveTab('orders')}
        >
          <ShoppingCartIcon fontSize="small" />
          سفارشات
        </button>
        <button
          className={activeTab === 'sales' ? 'filter-btn' : 'filter-btn outline'}
          onClick={() => setActiveTab('sales')}
        >
          <HistoryIcon fontSize="small" />
          تاریخچه فروش
        </button>
      </div>

      {activeTab === 'sales' && (
        <SalesHistoryTab onOpenOrder={(orderId, orderNumber) => openDetail({ _id: orderId, orderNumber } as Order)} />
      )}

      {activeTab === 'orders' && (
      <>
      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><ShoppingCartIcon /></div>
          <div>
            <p className="entity-stat-label">کل سفارشات</p>
            <p className="entity-stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            {/* ✅ FIX BUG-14: these counts are computed from the CURRENT page only, not totals. */}
            <p className="entity-stat-label">در انتظار پرداخت (این صفحه)</p>
            <p className="entity-stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><AutorenewIcon /></div>
          <div>
            <p className="entity-stat-label">در حال پردازش (این صفحه)</p>
            <p className="entity-stat-value">{stats.processing}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">تکمیل شده (این صفحه)</p>
            <p className="entity-stat-value">{stats.completed}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="شماره سفارش، شناسهٔ تراکنش اسنپ‌پی، ایمیل، نام محصول..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت سفارش</label>
            <select className="filter-field" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">همه</option>
              <option value="pending_payment">انتظار پرداخت</option>
              <option value="processing">پردازش</option>
              <option value="delivered">تحویل شده</option>
              <option value="completed">تکمیل شده</option>
              <option value="cancelled">لغو شده</option>
              <option value="disputed">اختلاف</option>
              <option value="refunded">مرجوع</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت پرداخت</label>
            <select className="filter-field" value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }}>
              <option value="">همه</option>
              <option value="pending">در انتظار</option>
              <option value="paid">پرداخت شده</option>
              <option value="failed">ناموفق</option>
              <option value="refunded">مرجوع</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">از تاریخ</label>
            <JalaliDatePicker
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); setPage(1); }}
              placeholder="تاریخ شروع"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">تا تاریخ</label>
            <JalaliDatePicker
              value={dateTo}
              onChange={(v) => { setDateTo(v); setPage(1); }}
              placeholder="تاریخ پایان"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {search && <span className="filter-tag">جستجو: {search}<button onClick={() => { setSearch(''); setSearchInput(''); }}>×</button></span>}
            {statusFilter && <span className="filter-tag">وضعیت: {orderStatusLabels[statusFilter]}<button onClick={() => setStatusFilter('')}>×</button></span>}
            {paymentFilter && <span className="filter-tag">پرداخت: {paymentStatusLabels[paymentFilter]}<button onClick={() => setPaymentFilter('')}>×</button></span>}
            {dateFrom && <span className="filter-tag">از: {formatJalaliShort(dateFrom)}<button onClick={() => setDateFrom('')}>×</button></span>}
            {dateTo && <span className="filter-tag">تا: {formatJalaliShort(dateTo)}<button onClick={() => setDateTo('')}>×</button></span>}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : orders.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><ShoppingCartIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">سفارشی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>شماره سفارش</th>
                  <th>مشتری</th>
                  <th>مبلغ</th>
                  <th>وضعیت</th>
                  <th>پرداخت</th>
                  <th>تاریخ</th>
                  <th style={{ width: 120 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{order.orderNumber}</span>
                      {order.paymentDetails?.transactionId && gatewayRefundProvider(order) && (
                        <div
                          style={{ fontSize: 11, color: 'var(--color-text-secondary)', direction: 'ltr', textAlign: 'right', marginTop: 2 }}
                          title={`شناسهٔ تراکنش ${GATEWAY_LABEL[gatewayRefundProvider(order)!]}`}
                        >
                          {order.paymentDetails.transactionId}
                        </div>
                      )}
                      {order.paymentPlan?.mode === 'split' && (
                        <div style={{ fontSize: 10, marginTop: 3, display: 'inline-block', padding: '1px 6px', borderRadius: 999, background: order.paymentPlan.status === 'partially_paid' ? 'var(--color-warning-bg, #fff3e0)' : order.paymentPlan.status === 'completed' ? 'var(--color-success-bg, #e8f5e9)' : 'var(--color-bg-default)', color: order.paymentPlan.status === 'partially_paid' ? 'var(--color-warning-dark, #b45309)' : order.paymentPlan.status === 'completed' ? 'var(--color-success-dark, #2e7d32)' : 'var(--color-text-secondary)' }}>
                          ترکیبی · {order.paymentPlan.status === 'partially_paid' ? 'منتظر مرحلهٔ ۲' : order.paymentPlan.status === 'completed' ? 'کامل' : order.paymentPlan.status === 'pending' ? 'مرحلهٔ ۱' : order.paymentPlan.status === 'expired' ? 'منقضی' : 'لغو'}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{order.userId?.name || order.userId?.email || '-'}</div>
                      {order.userId?.name && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{order.userId.email}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{formatPrice(order.totalAmount)} تومان</div>
                      {order.figaPointsAwarded > 0 && (
                        <div style={{ fontSize: 11, color: '#28a745' }}>+{order.figaPointsAwarded} امتیاز</div>
                      )}
                    </td>
                    <td>
                      <span className={`tag tag-sm ${getOrderStatusTag(order.status)}`}>
                        {orderStatusLabels[order.status] || order.status}
                      </span>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${getPaymentStatusTag(order.paymentStatus)}`}>
                        {paymentStatusLabels[order.paymentStatus] || order.paymentStatus}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{formatDate(order.createdAt)}</td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => openDetail(order)}>
                            <InfoOutlinedIcon />
                          </button>
                        </Tooltip>
                        {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.REFUNDED && (
                          <Tooltip title="تغییر وضعیت" arrow>
                            <button className="action-btn toggle" onClick={() => { setDetail(order); setNewStatus(order.status); setStatusDialogOpen(true); }}>
                              <EditIcon />
                            </button>
                          </Tooltip>
                        )}
                        {order.paymentStatus === PaymentStatus.PAID && order.status !== OrderStatus.REFUNDED && (
                          <Tooltip title="بازپرداخت" arrow>
                            <button className="action-btn delete" onClick={() => openRefundDialog(order)}>
                              <AccountBalanceIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {orders.map((order) => (
                <div key={order._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      <ReceiptIcon />
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{order.orderNumber}</div>
                      {order.paymentDetails?.transactionId && gatewayRefundProvider(order) && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', direction: 'ltr', textAlign: 'right' }}>
                          {order.paymentDetails.transactionId}
                        </div>
                      )}
                      <div className="entity-card-desc">{order.userId?.name || order.userId?.email || '-'}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)', marginTop: 4 }}>
                        {formatPrice(order.totalAmount)} تومان
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${getOrderStatusTag(order.status)}`}>
                        {orderStatusLabels[order.status] || order.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '0 12px 8px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span className={`tag tag-sm ${getPaymentStatusTag(order.paymentStatus)}`}>
                      {paymentStatusLabels[order.paymentStatus]}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn" onClick={() => openDetail(order)}>
                      <InfoOutlinedIcon fontSize="small" /> جزئیات
                    </button>
                    {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.REFUNDED && (
                      <button className="entity-card-btn primary" onClick={() => { setDetail(order); setNewStatus(order.status); setStatusDialogOpen(true); }}>
                        <EditIcon fontSize="small" /> وضعیت
                      </button>
                    )}
                    {order.paymentStatus === PaymentStatus.PAID && order.status !== OrderStatus.REFUNDED && (
                      <button className="entity-card-btn danger" onClick={() => openRefundDialog(order)}>
                        <AccountBalanceIcon fontSize="small" /> بازپرداخت
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {total}
              </div>
              <div className="pagination-controls">
                <button className="pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages}</span>
                <button className="pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth className="order-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات سفارش {detail?.orderNumber}</span>
          <button className="details-close" onClick={() => setDetailOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {detailLoading ? (
            <div className="entity-loading" style={{ padding: 40 }}><CircularProgress size={32} /></div>
          ) : detail ? (
            <div className="edit-form">
              {/* Order Header Card */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات سفارش</div>
                <div className="edit-card-body">
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ width: 60, height: 60, borderRadius: 8, background: 'var(--color-primary-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <ReceiptIcon style={{ fontSize: 32, color: 'var(--color-primary)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: 0, marginBottom: 8 }}>{detail.orderNumber}</h3>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <span className={`tag tag-sm ${getOrderStatusTag(detail.status)}`}>
                          {orderStatusLabels[detail.status]}
                        </span>
                        <span className={`tag tag-sm ${getPaymentStatusTag(detail.paymentStatus)}`}>
                          {paymentStatusLabels[detail.paymentStatus]}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        ثبت: {formatDate(detail.createdAt)} • بروزرسانی: {formatDate(detail.updatedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Info Card */}
              <div className="edit-card">
                <div className="edit-card-header"><PersonIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: 'middle' }} /> اطلاعات مشتری</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{detail.userId?.name || '-'}</div>
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{detail.userId?.email || '-'}</div>
                    </div>
                    <div className="edit-field">
                      <label>تلفن</label>
                      <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{detail.userId?.phone || '-'}</div>
                    </div>
                    {detail.figaPointsAwarded > 0 && (
                      <div className="edit-field">
                        <label>امتیاز اعطایی</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-success-bg)', borderRadius: 6, fontSize: 13, color: 'var(--color-success-text)' }}>
                          +{detail.figaPointsAwarded} امتیاز
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Summary Card */}
              <div className="edit-card">
                <div className="edit-card-header">خلاصه مالی</div>
                <div className="edit-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                    <div style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>جمع کالاها</div>
                      <div style={{ fontWeight: 600 }}>{formatPrice(detail.subtotal)} تومان</div>
                    </div>
                    <div style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>مالیات</div>
                      <div style={{ fontWeight: 600 }}>{formatPrice(detail.tax)} تومان</div>
                    </div>
                    <div style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>کمیسیون</div>
                      <div style={{ fontWeight: 600 }}>{formatPrice(detail.commission)} تومان</div>
                    </div>
                    <div style={{ padding: 12, background: 'var(--color-primary-bg)', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--color-primary)', marginBottom: 4 }}>مبلغ نهایی سفارش</div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-primary)' }}>{formatPrice(detail.totalAmount)} تومان</div>
                    </div>
                  </div>

                  {/* ── کارمزد پرداخت خریدار (خرید اقساطی دیجی‌پی) — خارج از مبلغ سفارش، با snapshot درصد/زمان ── */}
                  {detail.paymentSurcharge && detail.paymentSurcharge.amount > 0 && (
                    <div style={{ marginTop: 12, padding: 12, background: 'var(--color-warning-bg, #fff8e1)', border: '1px solid var(--color-warning, #ffb300)', borderRadius: 6 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, fontSize: 12 }}>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>کارمزد {INSTRUMENT_LABELS[detail.paymentSurcharge.instrument] || detail.paymentSurcharge.instrument}</div>
                          <div style={{ fontWeight: 700 }}>{formatPrice(detail.paymentSurcharge.amount)} تومان</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>درصد لحظهٔ پرداخت</div>
                          <div style={{ fontWeight: 700 }}>{toPersianDigits(String(detail.paymentSurcharge.ratePercent))}٪ ({detail.paymentSurcharge.mode === 'gross_up' ? 'جبرانی' : 'روی مبلغ سفارش'})</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>مبلغ پرداختی خریدار</div>
                          <div style={{ fontWeight: 700, color: 'var(--color-primary)' }}>{formatPrice(detail.paymentDetails?.paidAmount || detail.payableAmount || detail.totalAmount + detail.paymentSurcharge.amount)} تومان</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>وضعیت کارمزد</div>
                          <div style={{ fontWeight: 700 }}>
                            {detail.paymentSurcharge.status === 'charged' && 'دریافت‌شده'}
                            {detail.paymentSurcharge.status === 'planned' && 'در انتظار پرداخت'}
                            {detail.paymentSurcharge.status === 'adjusted' && `تعدیل‌شده — ${formatPrice(detail.paymentSurcharge.adjustedAmount || 0)} به کیف پول`}
                            {detail.paymentSurcharge.status === 'refunded' && `بازگشت‌داده‌شده — ${formatPrice(detail.paymentSurcharge.adjustedAmount || 0)} به کیف پول`}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>زمان دریافت</div>
                          <div style={{ fontWeight: 600 }}>{detail.paymentSurcharge.chargedAt ? formatDate(detail.paymentSurcharge.chargedAt) : '—'}</div>
                        </div>
                        {detail.paymentSurcharge.actualInstrument && detail.paymentSurcharge.actualInstrument !== detail.paymentSurcharge.instrument && (
                          <div>
                            <div style={{ color: 'var(--color-text-secondary)', marginBottom: 2 }}>ابزار واقعی پرداخت</div>
                            <div style={{ fontWeight: 600 }}>{INSTRUMENT_LABELS[detail.paymentSurcharge.actualInstrument] || detail.paymentSurcharge.actualInstrument}</div>
                          </div>
                        )}
                      </div>
                      {detail.paymentSurcharge.status === 'charged' && detail.paymentStatus === 'paid' && (
                        <div style={{ marginTop: 10 }}>
                          <button className="action-btn delete" onClick={handleRefundSurcharge} disabled={surchargeRefundLoading}>
                            {surchargeRefundLoading ? 'در حال واریز…' : 'بازگشت کارمزد به کیف پول خریدار'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── پرداخت ترکیبی (نقدی + اقساطی): مراحل، وضعیت، مهلت، تاریخچه و اکشن‌های ادمین ── */}
                  {detail.paymentPlan?.mode === 'split' && (() => {
                    const plan = detail.paymentPlan!;
                    const PLAN_LABEL: Record<string, string> = { pending: 'در انتظار مرحلهٔ اول (نقدی)', partially_paid: 'مرحلهٔ اول انجام شد — منتظر مرحلهٔ دوم', completed: 'هر دو مرحله پرداخت شد', cancelled: 'لغو شد', expired: 'مهلت تمام شد' };
                    const PLAN_COLOR: Record<string, string> = { pending: 'var(--color-warning-dark, #b45309)', partially_paid: 'var(--color-warning-dark, #b45309)', completed: 'var(--color-success-dark, #2e7d32)', cancelled: 'var(--color-text-secondary)', expired: 'var(--color-text-secondary)' };
                    const LEG_LABEL: Record<string, string> = { cash: 'بخش نقدی (زرین‌پال)', bnpl: 'بخش اقساطی', remainder: 'باقی‌مانده (نقدی)' };
                    const LEG_STATUS: Record<string, string> = { pending: 'در انتظار', paid: 'پرداخت شد', failed: 'ناموفق', refunded: 'برگشت داده شد', skipped: 'جایگزین شد' };
                    const GW: Record<string, string> = { zarinpal: 'زرین‌پال', snapppay: 'اسنپ‌پی', digipay: 'دیجی‌پی', wallet: 'کیف پول' };
                    const remainingMs = plan.expiresAt ? new Date(plan.expiresAt).getTime() - Date.now() : null;
                    const stuck = ['cancelled', 'expired'].includes(plan.status) && plan.legs.some((l) => l.gateway === 'zarinpal' && l.status === 'paid' && (l.amount - (l.refundedAmount || 0)) > 0);
                    return (
                      <div style={{ marginTop: 12, padding: 12, background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.35)', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 700 }}>
                          <span style={{ color: '#7c3aed' }}>⇄</span> پرداخت ترکیبی (نقدی + اقساطی با {GW[plan.bnplGateway]})
                          <span style={{ marginRight: 'auto', fontWeight: 700, color: PLAN_COLOR[plan.status] }}>{PLAN_LABEL[plan.status] || plan.status}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 10 }}>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>جمع قابل پرداخت</div><div style={{ fontWeight: 600 }}>{formatPrice(plan.totalPayable)} تومان</div></div>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>پرداخت‌شده تاکنون</div><div style={{ fontWeight: 600 }}>{formatPrice(plan.legs.filter((l) => l.status === 'paid').reduce((s, l) => s + l.amount, 0))} تومان</div></div>
                          {plan.expiresAt && plan.status === 'partially_paid' && (
                            <div><div style={{ color: 'var(--color-text-secondary)' }}>مهلت مرحلهٔ دوم</div><div style={{ fontWeight: 600, color: remainingMs !== null && remainingMs <= 0 ? 'var(--color-error-dark)' : undefined }}>{formatDate(plan.expiresAt)}{remainingMs !== null && remainingMs > 0 ? ` (${toPersianDigits(String(Math.ceil(remainingMs / 3600000)))} ساعت مانده)` : ' (تمام شده)'}</div></div>
                          )}
                          {plan.cancelledAt && <div><div style={{ color: 'var(--color-text-secondary)' }}>لغو</div><div style={{ fontWeight: 600 }}>{formatDate(plan.cancelledAt)} — {plan.cancelledBy === 'buyer' ? 'خریدار' : plan.cancelledBy === 'admin' ? 'ادمین' : 'سیستم'}{plan.cancelReason ? ` · ${plan.cancelReason}` : ''}</div></div>}
                          {plan.completedAt && <div><div style={{ color: 'var(--color-text-secondary)' }}>تکمیل</div><div style={{ fontWeight: 600 }}>{formatDate(plan.completedAt)}</div></div>}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
                          <thead><tr style={{ color: 'var(--color-text-secondary)', textAlign: 'right' }}><th style={{ padding: 4 }}>مرحله</th><th style={{ padding: 4 }}>درگاه</th><th style={{ padding: 4 }}>مبلغ</th><th style={{ padding: 4 }}>وضعیت</th><th style={{ padding: 4 }}>شناسه</th><th style={{ padding: 4 }}>برگشت</th></tr></thead>
                          <tbody>
                            {plan.legs.map((l, i) => (
                              <tr key={l.key} style={{ borderTop: '1px solid var(--color-border)' }}>
                                <td style={{ padding: 4, fontWeight: 600 }}>{toPersianDigits(String(i + 1))} · {LEG_LABEL[l.key] || l.key}</td>
                                <td style={{ padding: 4 }}>{GW[l.gateway] || l.gateway}</td>
                                <td style={{ padding: 4 }}>{formatPrice(l.amount)}{l.surchargeAmount > 0 ? <span style={{ color: 'var(--color-text-secondary)' }}> (کارمزد {formatPrice(l.surchargeAmount)})</span> : null}</td>
                                <td style={{ padding: 4, color: l.status === 'paid' ? 'var(--color-success-dark, #2e7d32)' : l.status === 'failed' ? 'var(--color-error-dark)' : undefined }}>{LEG_STATUS[l.status] || l.status}{l.paidAt ? ` · ${formatDate(l.paidAt)}` : ''}{l.lastError ? <div style={{ color: 'var(--color-error-dark)', fontSize: 11 }}>{l.lastError}</div> : null}</td>
                                <td style={{ padding: 4, direction: 'ltr', textAlign: 'right', wordBreak: 'break-all', fontSize: 11 }}>{l.refId || l.authority || '—'}</td>
                                <td style={{ padding: 4 }}>{(l.refundedAmount || 0) > 0 ? `${formatPrice(l.refundedAmount || 0)} (${GW[l.refundChannel || ''] || l.refundChannel || '—'})` : '—'}{l.refundError ? <div style={{ color: 'var(--color-error-dark)', fontSize: 11 }}>خطا: {l.refundError}</div> : null}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {stuck && <div style={{ padding: 8, background: 'var(--color-error-bg, #fdecea)', borderRadius: 6, marginBottom: 8, color: 'var(--color-error-dark)' }}>بخش نقدی پرداخت‌شده هنوز به کیف پول خریدار واریز نشده است (واریز خودکار خطا داده)؛ کرون دوباره تلاش می‌کند — یا همین حالا «واریز به کیف پول خریدار» را بزنید.</div>}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {['pending', 'partially_paid'].includes(plan.status) && (
                            <>
                              <button className="action-btn delete" disabled={!!splitBusy} onClick={handleSplitCancel}>{splitBusy === 'cancel' ? 'در حال لغو…' : 'لغو پرداخت ترکیبی + واریز بخش نقدی به کیف پول'}</button>
                              {plan.status === 'partially_paid' && <button className="action-btn" disabled={!!splitBusy} onClick={handleSplitExtend}>{splitBusy === 'extend' ? '…' : 'تمدید مهلت مرحلهٔ دوم'}</button>}
                              {plan.status === 'partially_paid' && <button className="action-btn" disabled={!!splitBusy} onClick={() => splitCall('remind')}>{splitBusy === 'remind' ? '…' : 'ارسال یادآوری به خریدار'}</button>}
                            </>
                          )}
                          {stuck && <button className="action-btn" disabled={!!splitBusy} onClick={() => splitCall('retry-cash-refund')}>{splitBusy === 'retry-cash-refund' ? '…' : 'واریز بخش نقدی به کیف پول خریدار'}</button>}
                        </div>
                        {plan.status === 'completed' && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, marginBottom: 8 }}>
                              استرداد این سفارش با «بازپرداخت سفارش/آیتم» انجام می‌شود و خودکار بین دو مرحله تقسیم می‌گردد: اول از بخش اقساطی ({GW[plan.bnplGateway]})، سپس بخش نقدی به کیف پول خریدار.
                            </div>
                            {detail.paymentStatus === 'paid' && (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button className="action-btn delete" disabled={!!splitBusy} onClick={() => handleUnwind('none')}>
                                  {splitBusy === 'unwind' ? 'در حال لغو…' : 'لغو کامل سفارش + بازگشت کل مبلغ (هر دو مرحله)'}
                                </button>
                                <button className="action-btn" disabled={!!splitBusy} onClick={() => handleUnwind('bnpl')}>
                                  {GW[plan.bnplGateway]} پول را پس گرفته — فقط سهم نقدی به کیف پول
                                </button>
                                <button className="action-btn" disabled={!!splitBusy} onClick={() => handleUnwind('cash')}>
                                  سهم نقدی برگشت خورده — فقط سهم اقساطی از {GW[plan.bnplGateway]}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {Array.isArray(plan.refunds) && plan.refunds.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>استردادهای انجام‌شده</div>
                            {plan.refunds.map((r) => (
                              <div key={r.key} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                {formatDate(r.at)} · درخواست {formatPrice(r.requested)} ⇒ اقساطی {formatPrice(r.bnplAmount)} {r.bnplDone ? '✓' : '✗'} · نقدی {formatPrice(r.cashAmount)} {r.cashDone ? '✓' : '✗'}{r.error ? ` · خطا: ${r.error}` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        {Array.isArray(plan.history) && plan.history.length > 0 && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>تاریخچهٔ رویدادها ({toPersianDigits(String(plan.history.length))})</summary>
                            <div style={{ marginTop: 6, maxHeight: 220, overflow: 'auto' }}>
                              {[...plan.history].reverse().map((h, i) => (
                                <div key={i} style={{ fontSize: 11, padding: '3px 0', borderBottom: '1px dashed var(--color-border)' }}>
                                  <span style={{ color: 'var(--color-text-secondary)' }}>{formatDate(h.at)}</span> · <b>{h.event}</b> · {h.by === 'buyer' ? 'خریدار' : h.by === 'admin' ? 'ادمین' : 'سیستم'}{h.amount ? ` · ${formatPrice(h.amount)} تومان` : ''}{h.note ? ` — ${h.note}` : ''}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── اطلاعات درگاه (اسنپ‌پی — خرید اقساطی BNPL) ── */}
                  {detail.paymentDetails?.provider === 'snapppay' && (
                    <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 700 }}>
                        <img src="/snapppay-logo.svg" alt="اسنپ‌پی" width={24} height={24} style={{ borderRadius: 6 }} />
                        اسنپ‌پی — خرید اقساطی
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>وضعیت در اسنپ‌پی</div><div style={{ fontWeight: 600, color: detail.paymentDetails.snapppayStatus === 'SETTLE' ? 'var(--color-success-dark, #2e7d32)' : detail.paymentDetails.snapppayStatus === 'CANCEL' || detail.paymentDetails.snapppayStatus === 'REVERT' ? 'var(--color-error-dark)' : 'var(--color-warning-dark, #b45309)' }}>{SNAPPPAY_STATUS_LABELS[detail.paymentDetails.snapppayStatus || ''] || detail.paymentDetails.snapppayStatus || '—'}</div></div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)' }}>شناسهٔ تراکنش (ما ↔ اسنپ‌پی)</div>
                          <div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {detail.paymentDetails.transactionId || '—'}
                            {detail.paymentDetails.transactionId && (
                              <button className="action-btn" title="کپی" onClick={() => copyText(detail.paymentDetails!.transactionId!)}><ContentCopyIcon /></button>
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)' }}>توکن پرداخت</div>
                          <div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right', wordBreak: 'break-all', display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <span>{snappToken || '—'}</span>
                            {snappToken && <button className="action-btn" title="کپی" onClick={() => copyText(snappToken)}><ContentCopyIcon /></button>}
                          </div>
                        </div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>مبلغ سفارش (کالا)</div><div style={{ fontWeight: 600 }}>{formatPrice(detail.totalAmount)} تومان</div></div>
                        <div>
                          <div style={{ color: 'var(--color-text-secondary)' }}>کارمزد خرید اقساطی</div>
                          <div style={{ fontWeight: 600 }}>
                            {detail.paymentSurcharge && detail.paymentSurcharge.amount > 0
                              ? `${formatPrice(detail.paymentSurcharge.amount)} تومان (${detail.paymentSurcharge.ratePercent}٪)${['refunded', 'adjusted'].includes(String(detail.paymentSurcharge.status)) ? ' — برگشت‌خورده' : ''}`
                              : 'بدون کارمزد'}
                          </div>
                        </div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>مبلغ فعلی نزد اسنپ‌پی</div><div style={{ fontWeight: 800 }}>{formatPrice(detail.paymentDetails.paidAmount ?? detail.payableAmount ?? detail.totalAmount)} تومان</div></div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>زمان تأیید (verify)</div><div style={{ fontWeight: 600 }}>{detail.paymentDetails.verifiedAt ? formatDate(detail.paymentDetails.verifiedAt) : '—'}</div></div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>نهایی‌سازی (settle)</div><div style={{ fontWeight: 600 }}>{detail.paymentDetails.snapppaySettledAt ? formatDate(detail.paymentDetails.snapppaySettledAt) : 'هنوز انجام نشده'}</div></div>
                        {detail.paymentDetails.snapppayCancelledAt && <div><div style={{ color: 'var(--color-text-secondary)' }}>لغو / برگشت</div><div style={{ fontWeight: 600 }}>{formatDate(detail.paymentDetails.snapppayCancelledAt)}</div></div>}
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>وضعیت سفارش در گمیزو</div><div style={{ fontWeight: 600 }}>{orderStatusLabels[detail.status] || detail.status} / {paymentStatusLabels[detail.paymentStatus] || detail.paymentStatus}</div></div>
                      </div>
                      {detail.paymentDetails.snapppaySyncError && (
                        <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 8, background: 'var(--color-warning-bg, #fff8e1)', color: 'var(--color-warning-dark, #8a5a00)', fontSize: 12, lineHeight: 1.9 }}>
                          ⚠️ {detail.paymentDetails.snapppaySyncError}
                        </div>
                      )}
                      {detail.paymentStatus !== 'paid' && (
                        <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 8, background: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)', fontSize: 12, lineHeight: 1.9 }}>
                          پرداخت این سفارش با اسنپ‌پی نهایی نشده است؛ فقط استعلام وضعیت و مشاهدهٔ لاگ‌ها معنا دارد و عملیات مالی در دسترس نیست.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        <button className="action-btn" disabled={!!snappBusy || !snappToken} title={snappToken ? '' : 'توکن پرداخت روی این سفارش ثبت نشده است'} onClick={() => snappCall('status')}>{snappBusy === 'status' ? 'در حال استعلام…' : 'استعلام وضعیت (Get Payment Status)'}</button>
                        {detail.paymentDetails.snapppayStatus !== 'SETTLE' && detail.paymentDetails.snapppayStatus !== 'CANCEL' && detail.paymentDetails.snapppayStatus !== 'REVERT' && (
                          <button className="action-btn" disabled={!!snappBusy} onClick={() => setSnappConfirm({
                            title: 'نهایی‌سازی تراکنش در اسنپ‌پی (settle)',
                            description: 'با نهایی‌سازی، خرید نزد اسنپ‌پی قطعی می‌شود و مبلغ به حساب فروشگاه می‌نشیند. پس از این مرحله «بازگشت وجه قبل از نهایی‌سازی (revert)» دیگر ممکن نیست و برای استرداد فقط «لغو کامل» یا «کاهش مبلغ» در دسترس است.',
                            rows: [
                              { label: 'شماره سفارش', value: String(detail.orderNumber || '—') },
                              { label: 'مبلغ', value: t(detail.paymentDetails?.paidAmount || detail.totalAmount) },
                            ],
                            irreversibleNote: 'پس از settle امکان revert وجود ندارد.',
                            confirmLabel: 'نهایی‌سازی تراکنش',
                            run: () => { setSnappConfirm(null); snappCall('settle'); },
                          })}>{snappBusy === 'settle' ? 'در حال نهایی‌سازی…' : 'نهایی‌سازی (settle)'}</button>
                        )}
                        {detail.paymentDetails.snapppayStatus !== 'CANCEL' && detail.paymentDetails.snapppayStatus !== 'REVERT' && (
                          <>
                            <button className="action-btn" disabled={!!snappBusy} onClick={handleSnappUpdate}>{snappBusy === 'update' ? 'در حال به‌روزرسانی…' : 'کاهش مبلغ (update)'}</button>
                            <button className="action-btn" disabled={!!snappBusy} style={{ color: 'var(--color-error-dark)' }} onClick={handleSnappCancel}>{snappBusy === 'cancel' ? 'در حال لغو…' : 'لغو کامل در اسنپ‌پی (cancel)'}</button>
                          </>
                        )}
                        {(!detail.paymentDetails.snapppayStatus || detail.paymentDetails.snapppayStatus === 'PENDING' || detail.paymentDetails.snapppayStatus === 'VERIFY') && (
                          <button className="action-btn" disabled={!!snappBusy} style={{ color: 'var(--color-warning-dark, #b45309)' }} onClick={handleSnappRevert}>{snappBusy === 'revert' ? 'در حال revert…' : 'بازگشت وجه قبل از نهایی‌سازی (revert)'}</button>
                        )}
                        <button className="action-btn" disabled={snappLogsLoading} onClick={() => loadSnappLogs(detail._id)}>
                          {snappLogsLoading ? 'در حال دریافت…' : snappLogs ? 'تازه‌سازی لاگ‌ها' : 'لاگ‌های اسنپ‌پی این سفارش'}
                        </button>
                        <button className="action-btn" onClick={() => navigate(`/snapppay?tab=1&search=${encodeURIComponent(detail.paymentDetails?.transactionId || detail.orderNumber)}`)}>مدیریت در مرکز اسنپ‌پی</button>
                        {snappLogs && (
                          <div style={{ width: '100%', marginTop: 10, border: '1px solid var(--color-border)', borderRadius: 9, overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 540 }}>
                              <thead>
                                <tr style={{ color: 'var(--color-text-secondary)', textAlign: 'right' }}>
                                  <th style={{ padding: 7, fontWeight: 700 }}>زمان</th>
                                  <th style={{ padding: 7, fontWeight: 700 }}>عملیات</th>
                                  <th style={{ padding: 7, fontWeight: 700 }}>وضعیت</th>
                                  <th style={{ padding: 7, fontWeight: 700 }}>مبلغ</th>
                                  <th style={{ padding: 7, fontWeight: 700 }}>پیام</th>
                                </tr>
                              </thead>
                              <tbody>
                                {snappLogs.length === 0 && (
                                  <tr><td colSpan={5} style={{ padding: 10, textAlign: 'center', color: 'var(--color-text-secondary)' }}>لاگی برای این سفارش ثبت نشده است</td></tr>
                                )}
                                {snappLogs.map((lg: any) => (
                                  <tr key={lg._id} style={{ borderTop: '1px solid var(--color-border)' }}>
                                    <td style={{ padding: 7, whiteSpace: 'nowrap' }}>{formatDate(lg.createdAt)}</td>
                                    <td style={{ padding: 7, whiteSpace: 'nowrap' }}>{lg.action}</td>
                                    <td style={{ padding: 7, whiteSpace: 'nowrap', color: lg.status === 'success' ? 'var(--color-success-dark, #2e7d32)' : lg.status === 'failed' ? 'var(--color-error-dark)' : undefined }}>{lg.status}</td>
                                    <td style={{ padding: 7, whiteSpace: 'nowrap' }}>{lg.amount ? formatPrice(lg.amount) : '—'}</td>
                                    <td style={{ padding: 7, wordBreak: 'break-word' }}>{lg.error || lg.message || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {/* ✏️ ویرایش اقلام: کاهش تعداد و حذف ردیف — فقط سفارش پرداخت‌شدهٔ اسنپ‌پی */}
                        {detail.paymentStatus === 'paid' && !['CANCEL', 'REVERT'].includes(String(detail.paymentDetails?.snapppayStatus || '')) && (
                          <div style={{ width: '100%', marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--color-border)' }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>ویرایش اقلام سفارش</div>
                            <SnappPayOrderEditor
                              items={(detail.items || []) as any}
                              format={formatPrice}
                              busy={!!snappBusy}
                              lastActionAt={detail.paymentDetails?.snapppayLastActionAt}
                              onSubmit={handleSnappEdit}
                              onCancelOrder={handleSnappCancel}
                            />
                          </div>
                        )}
                        {detail.paymentStatus === 'paid' && ['CANCEL', 'REVERT'].includes(String(detail.paymentDetails?.snapppayStatus || '')) && (
                          <button className="action-btn delete" disabled={!!splitBusy} onClick={() => handleUnwind('bnpl')}>
                            {splitBusy === 'unwind' ? 'در حال لغو…' : 'اسنپ‌پی پول را پس گرفته — لغو کامل سفارش و همگام‌سازی مالی'}
                          </button>
                        )}
                      </div>
                      <div style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 11, lineHeight: 1.8 }}>
                        لغو/کاهش مبلغ در اسنپ‌پی، مبلغ را به اعتبار خریدار نزد اسنپ‌پی برمی‌گرداند و برگشت‌ناپذیر است. «استرداد آیتم» و «بازپرداخت سفارش» برای این سفارش خودکار از طریق update/cancel اسنپ‌پی انجام می‌شود (نه کیف پول).
                      </div>
                    </div>
                  )}

                  {/* ── اطلاعات درگاه (دیجی‌پی) ── */}
                  {detail.paymentDetails?.provider === 'digipay' && (
                    <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>درگاه</div><div style={{ fontWeight: 600 }}>{PROVIDER_LABELS[detail.paymentDetails.provider] || detail.paymentDetails.provider} — {INSTRUMENT_LABELS[detail.paymentDetails.instrument || ''] || detail.paymentDetails.instrument || '—'}</div></div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>کد پیگیری دیجی‌پی</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{detail.paymentDetails.trackingCode || detail.paymentDetails.transactionId || '—'}</div></div>
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>شناسه ما (providerId)</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{detail.paymentDetails.gatewayReference || '—'}</div></div>
                        {detail.paymentDetails.rrn && <div><div style={{ color: 'var(--color-text-secondary)' }}>RRN</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{detail.paymentDetails.rrn}</div></div>}
                        {detail.paymentDetails.maskedPan && <div><div style={{ color: 'var(--color-text-secondary)' }}>کارت</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{detail.paymentDetails.maskedPan}</div></div>}
                        {detail.paymentDetails.pspName && <div><div style={{ color: 'var(--color-text-secondary)' }}>PSP</div><div style={{ fontWeight: 600 }}>{detail.paymentDetails.pspName}</div></div>}
                        <div><div style={{ color: 'var(--color-text-secondary)' }}>زمان تأیید</div><div style={{ fontWeight: 600 }}>{detail.paymentDetails.verifiedAt ? formatDate(detail.paymentDetails.verifiedAt) : '—'}</div></div>
                        {detail.paymentDetails.instrument === 'credit' && (
                          <div>
                            <div style={{ color: 'var(--color-text-secondary)' }}>ثبت تحویل در دیجی‌پی</div>
                            <div style={{ fontWeight: 600 }}>
                              {detail.paymentDetails.digipayDeliverReportedAt
                                ? formatDate(detail.paymentDetails.digipayDeliverReportedAt)
                                : (detail.paymentDetails.digipayDeliverError ? <span style={{ color: 'var(--color-error-dark)' }}>خطا: {detail.paymentDetails.digipayDeliverError}</span> : 'هنوز ثبت نشده')}
                            </div>
                            {!detail.paymentDetails.digipayDeliverReportedAt && (detail.status === 'delivered' || detail.status === 'completed') && (
                              <button className="action-btn" style={{ marginTop: 6 }} onClick={handleDigipayDeliver}>ثبت تحویل در دیجی‌پی</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── اطلاعات درگاه (زرین‌پال) — ریورس ≤۳۰ دقیقه / استرداد به کارت خریدار ── */}
                  {isZarinpalOrder(detail) && (() => {
                    const pd = detail.paymentDetails;
                    const refunds = pd.zarinpalRefunds || [];
                    const paid = pd.paidAmount || detail.totalAmount;
                    const viaZp = refunds.filter(r => r.status !== 'FAILED').reduce((a, r) => a + (r.amount || 0), 0);
                    const remainingMs = zpReverseRemainingMs(detail);
                    const canReverse = detail.paymentStatus === 'paid' && !pd.zarinpalReversedAt && viaZp === 0 && remainingMs > 0 && (zpConfig?.reverseEnabled ?? true);
                    const hasPending = refunds.some(r => r.status === 'PENDING');
                    return (
                      <div style={{ marginTop: 12, padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 700 }}>
                          <img src="/zarinpal-logo.png" alt="زرین‌پال" width={24} height={24} style={{ borderRadius: 6 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          زرین‌پال — درگاه بانکی
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>شمارهٔ تراکنش (ref_id)</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{pd.transactionId || '—'}</div></div>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>Authority</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right', wordBreak: 'break-all' }}>{pd.gatewayReference || '—'}</div></div>
                          {pd.maskedPan && <div><div style={{ color: 'var(--color-text-secondary)' }}>کارت</div><div style={{ fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>{pd.maskedPan}</div></div>}
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>مبلغ پرداخت‌شده</div><div style={{ fontWeight: 600 }}>{formatPrice(paid)} تومان</div></div>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>زمان تأیید</div><div style={{ fontWeight: 600 }}>{pd.verifiedAt || pd.paidAt ? formatDate((pd.verifiedAt || pd.paidAt) as string) : '—'}</div></div>
                          <div>
                            <div style={{ color: 'var(--color-text-secondary)' }}>مهلت ریورس (۳۰ دقیقه)</div>
                            <div style={{ fontWeight: 600, color: remainingMs > 0 ? 'var(--color-success-dark, #2e7d32)' : 'var(--color-text-secondary)' }}>
                              {pd.zarinpalReversedAt ? `ریورس شده — ${formatDate(pd.zarinpalReversedAt)}` : remainingMs > 0 ? `${toPersianDigits(String(Math.ceil(remainingMs / 60000)))} دقیقه باقی مانده` : 'تمام شده'}
                            </div>
                          </div>
                          <div><div style={{ color: 'var(--color-text-secondary)' }}>برگشت‌داده‌شده از درگاه</div><div style={{ fontWeight: 600 }}>{formatPrice(viaZp)} از {formatPrice(paid)} تومان</div></div>
                        </div>
                        {refunds.length > 0 && (
                          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {refunds.map((r) => (
                              <div key={r.refundId} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg-paper, #fff)' }}>
                                <span className={`tag tag-sm ${r.status === 'SUCCESS' ? 'tag-success' : r.status === 'FAILED' ? 'tag-danger' : 'tag-warning'}`}>{ZP_REFUND_STATUS_LABELS[r.status] || r.status}</span>
                                <strong>{r.kind === 'reverse' ? 'ریورس' : 'استرداد'} {formatPrice(r.amount)} تومان</strong>
                                {r.method && <span style={{ color: 'var(--color-text-secondary)' }}>{ZP_METHOD_LABELS[r.method] || r.method}</span>}
                                <span style={{ color: 'var(--color-text-secondary)' }}>{formatDate(r.createdAt)}</span>
                                {r.reason && <span style={{ color: 'var(--color-text-secondary)' }}>— {r.reason}</span>}
                                {r.fallbackWalletCredited && <span style={{ color: 'var(--color-warning-dark, #b45309)' }}>واریز جایگزین به کیف پول انجام شد</span>}
                                {r.error && !r.fallbackWalletCredited && <span style={{ color: 'var(--color-error-dark)' }}>{r.error}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {pd.gatewayRefundError && <div style={{ marginTop: 8, color: 'var(--color-error-dark)' }}>آخرین خطای درگاه: {pd.gatewayRefundError}</div>}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                          {canReverse && (
                            <button className="action-btn" disabled={!!zpBusy} style={{ color: 'var(--color-error-dark)' }} onClick={handleZpReverse}>{zpBusy === 'reverse' ? 'در حال ریورس…' : 'ریورس فوری به کارت خریدار (بدون کارمزد)'}</button>
                          )}
                          {detail.paymentStatus === 'paid' && !pd.zarinpalReversedAt && viaZp < paid && (
                            <button className="action-btn" disabled={!!zpBusy || !zpConfig?.refundEnabled} title={zpConfig?.refundEnabled ? '' : 'ZP_ACCESS_TOKEN / ZP_TERMINAL_ID روی سرور تنظیم نشده'} onClick={() => openRefundDialog(detail)}>استرداد به کارت خریدار (کامل/جزئی)</button>
                          )}
                          {hasPending && (
                            <button className="action-btn" disabled={!!zpBusy} onClick={handleZpStatus}>{zpBusy === 'status' ? 'در حال استعلام…' : 'استعلام وضعیت استردادها'}</button>
                          )}
                        </div>
                        <div style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 11, lineHeight: 1.8 }}>
                          ریورس فقط تا ۳۰ دقیقه بعد از پرداخت و برای کل مبلغ ممکن است (IP سرور باید در پنل زرین‌پال ثبت باشد). استرداد (کامل/جزئی) هر زمان از طریق API پنل زرین‌پال انجام می‌شود: «آنی به کارت» یا «پایا». استرداد اول «در حال پردازش» است و هر ۵ دقیقه به‌روزرسانی می‌شود؛ اگر زرین‌پال آن را ناموفق اعلام کند، مبلغ خودکار به کیف پول خریدار واریز می‌شود.
                          {!zpConfig?.refundEnabled && <strong> (استرداد API فعال نیست — ZP_ACCESS_TOKEN و ZP_TERMINAL_ID را روی سرور تنظیم کنید.)</strong>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* 💸 تعهدهای استرداد — بازپرداخت خریدار از حساب فروشنده */}
              {obligations.length > 0 && (
                <div className="edit-card">
                  <div className="edit-card-header">تعهدهای استرداد (پرداخت از حساب فروشنده)</div>
                  <div className="edit-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                    {obligations.map((ob) => (
                      <div key={ob._id} style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg-default)' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                          <span className={`tag tag-sm ${ob.status === 'paid' ? 'tag-success' : ob.status === 'cancelled' ? 'tag-neutral' : 'tag-warning'}`}>{OB_STATUS_LABELS[ob.status] || ob.status}</span>
                          <strong>{formatPrice(ob.amount)} تومان</strong>
                          <span style={{ color: 'var(--color-text-secondary)' }}>فروشگاه: {typeof ob.storeId === 'object' ? (ob.storeId?.name || '—') : '—'}</span>
                          <span style={{ color: 'var(--color-text-secondary)' }}>{formatDate(ob.createdAt)}</span>
                          {ob.reason && <span style={{ color: 'var(--color-text-secondary)' }}>— {ob.reason}</span>}
                        </div>
                        {ob.status === 'awaiting_funds' && ob.poolSnapshot && (
                          <div style={{ marginTop: 4, color: 'var(--color-text-secondary)' }}>
                            هنگام ثبت: موجودی نگه‌داشتهٔ فروشنده {formatPrice(ob.poolSnapshot.pool)} — تعهدهای قبلی {formatPrice(ob.poolSnapshot.outstandingBefore)} — کسری {formatPrice(ob.poolSnapshot.shortfall)} تومان. با هر فروش جدید فروشنده، ورودی قفل و به‌محض تأمین، خودکار به خریدار پرداخت می‌شود.
                          </div>
                        )}
                        {ob.status === 'paid' && (
                          <div style={{ marginTop: 4, color: 'var(--color-success-dark, #2e7d32)' }}>
                            {ob.paidAt ? formatDate(ob.paidAt) : ''} — {ob.outcome?.message || ob.outcome?.channelText || 'پرداخت شد'}{ob.forcedByAdminId ? ' (پرداخت از منابع پلتفرم توسط ادمین)' : ''}
                          </div>
                        )}
                        {ob.lastError && ob.status !== 'paid' && <div style={{ marginTop: 4, color: 'var(--color-error-dark)' }}>آخرین خطا: {ob.lastError}</div>}
                        {ob.status === 'awaiting_funds' && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                            <button className="action-btn" disabled={!!obBusy} onClick={() => obligationAction(ob._id, 'force-pay')}>{obBusy === ob._id + 'force-pay' ? 'در حال پرداخت…' : 'پرداخت همین حالا از منابع پلتفرم'}</button>
                            <button className="action-btn" disabled={!!obBusy} style={{ color: 'var(--color-error-dark)' }} onClick={() => obligationAction(ob._id, 'cancel')}>{obBusy === ob._id + 'cancel' ? 'در حال لغو…' : 'لغو تعهد'}</button>
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: 11, lineHeight: 1.8 }}>
                      سیاست: بازپرداخت خریدار از پولی که پلتفرم از فروشنده نگه داشته (اسکرو + قابل برداشت) پرداخت می‌شود. اگر کافی نباشد، تعهد ثبت می‌شود و ورودی‌های بعدی فروشنده قفل می‌ماند تا خودکار به خریدار برسد (به ترتیب نوبت). «پرداخت از منابع پلتفرم» یعنی خریدار همین حالا پول را می‌گیرد و فروشنده بدهکار می‌ماند.
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Info Card */}
              {detail.paymentDetails && (
                <div className="edit-card">
                  <div className="edit-card-header">اطلاعات پرداخت</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>روش پرداخت</label>
                        <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{detail.paymentDetails.method || '-'}</div>
                      </div>
                      {detail.paymentDetails.transactionId && (
                        <div className="edit-field">
                          <label>شناسه تراکنش</label>
                          <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13, direction: 'ltr', textAlign: 'right' }}>{detail.paymentDetails.transactionId}</div>
                        </div>
                      )}
                      {detail.paymentDetails.paidAt && (
                        <div className="edit-field">
                          <label>تاریخ پرداخت</label>
                          <div style={{ padding: '8px 12px', background: 'var(--color-bg-default)', borderRadius: 6, fontSize: 13 }}>{formatDate(detail.paymentDetails.paidAt)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Order Items Card */}
              <div className="edit-card">
                <div className="edit-card-header">
                  <ShoppingCartIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: 'middle' }} /> آیتم‌های سفارش ({detail.items?.length || 0})
                </div>
                <div className="edit-card-body">
                  {detail.items && detail.items.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {detail.items.map((item) => (
                        <div key={item._id} style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                                {item.productTitle}
                                {item.unitNumber && item.unitTotalInGroup && item.unitTotalInGroup > 1 && (
                                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-secondary)', marginRight: 6 }}>
                                    (واحد {item.unitNumber} از {item.unitTotalInGroup})
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                <StorefrontIcon style={{ fontSize: 12, marginLeft: 4, verticalAlign: 'middle' }} /> {item.storeName}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <span className={`tag tag-sm ${getItemStatusTag(item.status)}`}>
                                {itemStatusLabels[item.status] || item.status}
                              </span>
                              {item.fulfillmentType && (
                                <span className="tag tag-sm tag-info">
                                  {fulfillmentTypeLabels[item.fulfillmentType] || item.fulfillmentType}
                                </span>
                              )}
                              {item.fulfillmentType === 'account_delivery' && item.deliveryMethod && (
                                <span className="tag tag-sm" style={{ background: '#f3e5f5', color: '#6a1b9a' }}>
                                  {deliveryMethodLabels[item.deliveryMethod] || item.deliveryMethod}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, fontSize: 12 }}>
                            <div><span style={{ color: 'var(--color-text-secondary)' }}>تعداد:</span> {item.quantity}</div>
                            <div><span style={{ color: 'var(--color-text-secondary)' }}>قیمت واحد:</span> {formatPrice(item.unitPrice)}</div>
                            {!!item.discountAmount && item.discountAmount > 0 && (
                              <div>
                                <span style={{ color: 'var(--color-text-secondary)' }}>تخفیف:</span>{' '}
                                <span style={{ color: '#c62828' }}>
                                  {formatPrice(item.discountAmount)}{item.discountPercentage ? ` (${item.discountPercentage}٪)` : ''}
                                </span>
                              </div>
                            )}
                            <div><span style={{ color: 'var(--color-text-secondary)' }}>قیمت کل:</span> <strong>{formatPrice(item.totalPrice)}</strong></div>
                            {typeof item.sellerCommission === 'number' && (
                              <div><span style={{ color: 'var(--color-text-secondary)' }}>کمیسیون:</span> {formatPrice(item.sellerCommission)}</div>
                            )}
                            {typeof item.sellerNetAmount === 'number' && (
                              <div><span style={{ color: 'var(--color-text-secondary)' }}>سهم فروشنده:</span> {formatPrice(item.sellerNetAmount)}</div>
                            )}
                            <div><span style={{ color: 'var(--color-text-secondary)' }}>تعداد درخواستی:</span> {item.accountsRequested}</div>
                            <div><span style={{ color: 'var(--color-text-secondary)' }}>تحویل شده:</span> {item.accountsDelivered}</div>
                            {!!(item as any).refundedQuantity && (item as any).refundedQuantity > 0 && (
                              <div>
                                <span style={{ color: 'var(--color-text-secondary)' }}>تعداد مستردشده:</span>{' '}
                                <span style={{ color: '#e65100' }}>{formatPrice((item as any).refundedQuantity)}</span>
                              </div>
                            )}
                            {!!item.refundedAmount && item.refundedAmount > 0 && (
                              <div>
                                <span style={{ color: 'var(--color-text-secondary)' }}>بازپرداخت‌شده:</span>{' '}
                                <span style={{ color: '#e65100' }}>{formatPrice(item.refundedAmount)}</span>
                              </div>
                            )}
                            {item.escrowReleaseDate && (
                              <div>
                                <span style={{ color: 'var(--color-text-secondary)' }}>آزادسازی اسکرو:</span>{' '}
                                {formatDate(item.escrowReleaseDate)}
                              </div>
                            )}
                          </div>

                          {/* حجم/سایز انتخاب‌شده هنگام خرید */}
                          {item.selectedCapacity && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-info-bg, #e3f2fd)', borderRadius: 4, fontSize: 12 }}>
                              <strong>حجم / سایز انتخابی:</strong> {item.selectedCapacity.name}
                              {item.selectedCapacity.capacityKey && item.selectedCapacity.capacityKey !== item.selectedCapacity.key && (
                                <span style={{ marginRight: 8, color: 'var(--color-text-secondary)' }}>
                                  (کلید: {item.selectedCapacity.capacityKey})
                                </span>
                              )}
                              <span style={{ marginRight: 12 }}>قیمت این حجم / سایز: {formatPrice(item.selectedCapacity.price)} تومان</span>
                            </div>
                          )}

                          {/* اطلاعات واردشده توسط خریدار در چک‌اوت (مثلاً توضیحات سفارش) */}
                          {item.buyerProvidedData && Object.keys(item.buyerProvidedData).length > 0 && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-bg-paper, #fff)', border: '1px dashed var(--color-border)', borderRadius: 4, fontSize: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                اطلاعات واردشده توسط خریدار
                                {item.buyerProvidedAt && (
                                  <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 8 }}>
                                    ({formatDate(item.buyerProvidedAt)})
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                                {Object.entries(item.buyerProvidedData).map(([k, v]) => (
                                  <span key={k}>
                                    {k}: <strong style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{String(v)}</strong>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}


                          {/* وضعیت تحویل با QRCODE */}
                          {item.qrDelivery?.status && (
                            <div style={{ marginTop: 8, padding: 8, background: '#f3e5f5', borderRadius: 4, fontSize: 12, borderRight: '3px solid #6a1b9a' }}>
                              <div style={{ fontWeight: 600, color: '#6a1b9a', marginBottom: 4 }}>تحویل با QRCODE</div>
                              <span className={`tag tag-sm ${getQrStatusTag(item.qrDelivery.status)}`}>
                                {qrStatusLabels[item.qrDelivery.status] || item.qrDelivery.status}
                              </span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                {item.qrDelivery.startedAt && <span>شروع: {formatDate(item.qrDelivery.startedAt)}</span>}
                                {item.qrDelivery.requestedAt && <span>آخرین درخواست QR: {formatDate(item.qrDelivery.requestedAt)}</span>}
                                {item.qrDelivery.loginCodeSentAt && <span>ارسال کد ورود: {formatDate(item.qrDelivery.loginCodeSentAt)}</span>}
                                {item.qrDelivery.confirmedAt && <span>تأیید خریدار: {formatDate(item.qrDelivery.confirmedAt)}</span>}
                              </div>
                            </div>
                          )}

                          {/* اقلام دیجیتال تحویل‌شده */}
                          {item.deliveredAccounts && item.deliveredAccounts.length > 0 && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-success-bg, #e8f5e9)', borderRadius: 4, fontSize: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>اقلام تحویل‌شده ({item.deliveredAccounts.length})</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {item.deliveredAccounts.map((acc, i) => (
                                  <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 11 }}>
                                    <span>{formatDate(acc.deliveredAt)}</span>
                                    {acc.accountInfo?.additionalInfo?.method === 'qr' ? (
                                      <span style={{ color: '#6a1b9a', fontWeight: 600 }}>تحویل با QRCODE</span>
                                    ) : (
                                      acc.accountInfo?.username && (
                                        <span>
                                          نام کاربری: <strong style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{acc.accountInfo.username}</strong>
                                        </span>
                                      )
                                    )}
                                    {acc.deliveredByType && (
                                      <span style={{ color: 'var(--color-text-secondary)' }}>
                                        توسط {acc.deliveredByType === 'employee' ? 'کارمند فروشگاه' : 'مالک فروشگاه'}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* اطلاعیه‌های تحویل info_only */}
                          {item.infoNotices && item.infoNotices.length > 0 && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-bg-default)', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>اطلاعیه‌های تحویل ({item.infoNotices.length})</div>
                              {item.infoNotices.map((n, i) => (
                                <div key={i} style={{ marginBottom: 4 }}>
                                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{formatDate(n.sentAt)}:</span> {n.message}
                                </div>
                              ))}
                            </div>
                          )}

                          {item.fulfillmentType === 'postal_delivery' && item.postalDeliveryDetails && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-info-bg)', borderRadius: 4, fontSize: 12 }}>
                              <LocalShippingIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
                              {!!item.postalDeliveryDetails.shippingCost && (
                                <span>هزینه ارسال: {formatPrice(item.postalDeliveryDetails.shippingCost * (item.quantity || 1))} تومان</span>
                              )}
                              {(item.postalDeliveryDetails.trackingCode || item.postalDeliveryDetails.trackingNumber) && (
                                <span style={{ marginRight: 12 }}>
                                  کد رهگیری: <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{item.postalDeliveryDetails.trackingCode || item.postalDeliveryDetails.trackingNumber}</span>
                                </span>
                              )}
                              {item.postalDeliveryDetails.shippedAt && (
                                <span style={{ marginRight: 12 }}>ارسال: {formatDate(item.postalDeliveryDetails.shippedAt)}</span>
                              )}
                              {item.postalDeliveryDetails.deliveredAt && (
                                <span style={{ marginRight: 12 }}>تحویل: {formatDate(item.postalDeliveryDetails.deliveredAt)}</span>
                              )}
                              {item.postalDeliveryDetails.shippingAddress && (
                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                                  گیرنده: {item.postalDeliveryDetails.shippingAddress.recipientName || '-'}
                                  {item.postalDeliveryDetails.shippingAddress.recipientPhone && ` — ${item.postalDeliveryDetails.shippingAddress.recipientPhone}`}
                                  {item.postalDeliveryDetails.shippingAddress.city && ` — ${item.postalDeliveryDetails.shippingAddress.province || ''} ${item.postalDeliveryDetails.shippingAddress.city}`}
                                  {item.postalDeliveryDetails.shippingAddress.address && ` — ${item.postalDeliveryDetails.shippingAddress.address}`}
                                </div>
                              )}
                            </div>
                          )}

                          {item.disputeInfo && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-danger-bg, #ffebee)', borderRadius: 4, borderRight: '3px solid #c62828' }}>
                              <div style={{ fontSize: 12, color: '#c62828', fontWeight: 600, marginBottom: 4 }}>
                                <WarningIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} /> اختلاف
                              </div>
                              <div style={{ fontSize: 12 }}>دلیل: {disputeReasonLabels[item.disputeInfo.reason] || item.disputeInfo.reason}</div>
                              <div style={{ fontSize: 12 }}>توضیحات: {item.disputeInfo.description}</div>
                              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                                تاریخ: {formatDate(item.disputeInfo.openedAt)}
                              </div>
                            </div>
                          )}

                          {/* ✅ Postal-return management — only when this item ACTUALLY
                              has an active return flow (postalReturnInfo.status set).
                              Truthy-check on the parent object alone matches items that
                              have an empty subdoc from old Mongoose schema defaults. */}
                          {item.postalReturnInfo?.status && (
                            <AdminPostalReturnPanel orderItemId={item._id} />
                          )}

                          {item.status !== 'refunded' && item.status !== 'cancelled' && detail.paymentStatus === 'paid' && (
                            <button
                              onClick={() => openRefundItemDialog(detail, item)}
                              style={{
                                marginTop: 8,
                                padding: '6px 12px',
                                fontSize: 12,
                                background: 'var(--color-warning-bg, #fff3e0)',
                                color: '#e65100',
                                border: '1px solid #ffb74d',
                                borderRadius: 4,
                                cursor: 'pointer'
                              }}
                            >
                              بازپرداخت این آیتم
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-secondary)' }}>آیتمی یافت نشد</div>
                  )}
                </div>
              </div>

              {/* Order Timeline Card — تایم‌لاین کامل رویدادها از ثبت تا تحویل و اسکرو */}
              <div className="edit-card">
                <div className="edit-card-header">
                  <HistoryIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: 'middle' }} /> تایم‌لاین سفارش
                </div>
                <div className="edit-card-body">
                  <OrderTimeline orderId={detail._id} />
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn submit" onClick={() => setDetailOpen(false)}>بستن</button>
        </div>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth className="order-status-dialog">
        <div className="edit-dialog-title">
          <span>تغییر وضعیت سفارش</span>
          <button className="details-close" onClick={() => setStatusDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تغییر وضعیت</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>وضعیت جدید <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as OrderStatus)}>
                      <option value="pending_payment">در انتظار پرداخت</option>
                      <option value="processing">در حال پردازش</option>
                      <option value="partially_delivered">تحویل جزئی</option>
                      <option value="delivered">تحویل شده</option>
                      <option value="completed">تکمیل شده</option>
                      <option value="cancelled">لغو شده</option>
                      <option value="disputed">اختلاف</option>
                      <option value="refunded">مرجوع شده</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setStatusDialogOpen(false)}>انصراف</button>
          <button className="edit-btn submit" onClick={handleUpdateStatus} disabled={statusLoading}>
            {statusLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {statusLoading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
        </div>
      </Dialog>

      {/* تأیید دومرحله‌ای عملیات اسنپ‌پی (برگشت‌ناپذیر) — فقط سفارش‌های اسنپ‌پی */}
      <SnappPayConfirmDialog
        open={!!snappConfirm}
        onClose={() => setSnappConfirm(null)}
        onConfirm={() => snappConfirm?.run()}
        busy={!!snappBusy}
        title={snappConfirm?.title || ''}
        description={snappConfirm?.description || ''}
        rows={snappConfirm?.rows}
        irreversibleNote={snappConfirm?.irreversibleNote}
        confirmLabel={snappConfirm?.confirmLabel}
      />

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth className="order-status-dialog">
        <div className="edit-dialog-title">
          <span>بازپرداخت سفارش</span>
          <button className="details-close" onClick={() => setRefundDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">بازپرداخت</div>
              <div className="edit-card-body">
                {isZarinpalOrder(detail) ? (
                  <div style={{ padding: 12, background: 'var(--color-info-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>مقصد استرداد</div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, cursor: 'pointer' }}>
                      <input type="radio" name="refund-target" checked={refundTarget === 'gateway'} disabled={!zpConfig?.refundEnabled && zpReverseRemainingMs(detail) <= 0} onChange={() => setRefundTarget('gateway')} />
                      <span>
                        <strong>برگشت به کارت خریدار از زرین‌پال</strong>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          {zpReverseRemainingMs(detail) > 0 ? 'استرداد کامل ⇒ ریورس آنی و بدون کارمزد (تا ۳۰ دقیقه بعد از پرداخت)؛ ' : ''}
                          {zpConfig?.refundEnabled ? 'استرداد جزئی/بعد از مهلت ⇒ AddRefund زرین‌پال (اول «در حال پردازش»، سپس تأیید نهایی).' : 'استرداد API فعال نیست (ZP_ACCESS_TOKEN/ZP_TERMINAL_ID).'}
                        </div>
                      </span>
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                      <input type="radio" name="refund-target" checked={refundTarget === 'wallet'} onChange={() => setRefundTarget('wallet')} />
                      <span>واریز به کیف پول کاربر در سایت</span>
                    </label>
                    {refundTarget === 'gateway' && zpConfig?.refundEnabled && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span>روش واریز:</span>
                        <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value as 'CARD' | 'PAYA')} style={{ padding: '4px 8px' }}>
                          <option value="CARD">آنی به کارت (CARD)</option>
                          <option value="PAYA">پایا — سیکل بعدی (PAYA)</option>
                        </select>
                      </div>
                    )}
                  </div>
                ) : (
                  gatewayRefundProvider(detail) ? (
                    <div style={{ padding: 12, background: 'var(--color-error-bg, #fdecea)', border: '1px solid var(--color-error, #dc2626)', borderRadius: 8, marginBottom: 12, fontSize: 13, lineHeight: 1.9 }}>
                      <strong>این سفارش با {GATEWAY_LABEL[gatewayRefundProvider(detail)!]} پرداخت شده است.</strong> استرداد مستقیماً در {GATEWAY_LABEL[gatewayRefundProvider(detail)!]} انجام می‌شود
                      {gatewayRefundProvider(detail) === 'snapppay'
                        ? ' (استرداد کامل = cancel، استرداد جزئی = update) و مبلغ به اعتبار اسنپ‌پی خریدار برمی‌گردد؛ هیچ مبلغی به کیف پول کازماشاپ واریز نمی‌شود.'
                        : ' و مبلغ به همان ابزار پرداخت خریدار (کارت/کیف پول/اعتبار دیجی‌پی) برمی‌گردد؛ هیچ مبلغی به کیف پول کازماشاپ واریز نمی‌شود.'}
                      {' '}<strong>این عملیات برگشت‌ناپذیر است.</strong>
                    </div>
                  ) : (
                  <div style={{ padding: 12, background: 'var(--color-info-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                    این عملیات مبلغ را به کیف پول کاربر واریز می‌کند.
                  </div>
                ))}
                <SurchargeRefundSwitch order={detail} />
                <RefundPreviewSteps preview={refundPreview} loading={refundPreviewLoading} error={refundPreviewError} />
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>دلیل بازپرداخت <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      rows={3}
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="دلیل بازپرداخت را وارد کنید..."
                    />
                  </div>
                  <div className="edit-field full">
                    <label>مبلغ بازپرداخت (اختیاری)</label>
                    <input
                      type="number"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={detail ? `پیش‌فرض: ${formatPrice(detail.totalAmount)} تومان` : ''}
                    />
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      اگر خالی بگذارید، کل مبلغ بازپرداخت می‌شود
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setRefundDialogOpen(false)}>انصراف</button>
          <button className="edit-btn submit" onClick={handleRefund} disabled={refundLoading || !refundReason.trim()}>
            {refundLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {refundLoading ? 'در حال پردازش...' : 'بازپرداخت'}
          </button>
        </div>
      </Dialog>

      {/* Refund Item Dialog */}
      <Dialog open={refundItemDialogOpen} onClose={() => { setRefundItemDialogOpen(false); setSelectedItem(null); }} maxWidth="sm" fullWidth className="order-status-dialog">
        <div className="edit-dialog-title">
          <span>بازپرداخت آیتم</span>
          <button className="details-close" onClick={() => { setRefundItemDialogOpen(false); setSelectedItem(null); }}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedItem && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">بازپرداخت آیتم</div>
                <div className="edit-card-body">
                  <div style={{ padding: 12, background: 'var(--color-warning-bg, #fff3e0)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                    <strong>نکته:</strong> کل قیمت اعلامی محصول (شامل مالیات) + هزینه ارسال و در صورت وجود، هزینه بازرسی به‌صورت کامل {gatewayRefundProvider(detail) ? `از طریق ${GATEWAY_LABEL[gatewayRefundProvider(detail)!]} به خریدار برگردانده` : refundItemTarget === 'gateway' ? 'از طریق زرین‌پال به کارت خریدار برگردانده' : 'به کیف پول خریدار واریز'} می‌شود.
                  </div>
                  {gatewayRefundProvider(detail) && (
                    <div style={{ padding: 12, background: 'var(--color-error-bg, #fdecea)', border: '1px solid var(--color-error, #dc2626)', borderRadius: 8, marginBottom: 12, fontSize: 13, lineHeight: 1.9 }}>
                      <strong>این سفارش با {GATEWAY_LABEL[gatewayRefundProvider(detail)!]} پرداخت شده است.</strong>{' '}
                      {gatewayRefundProvider(detail) === 'snapppay'
                        ? 'با تأیید، مبلغ این آیتم با سرویس update از سفارش اسنپ‌پی کم می‌شود (و اگر آخرین آیتم فعال باشد، کل سفارش cancel می‌شود) و به اعتبار اسنپ‌پی خریدار برمی‌گردد.'
                        : 'با تأیید، مبلغ این آیتم از طریق سرویس بازگشت وجه دیجی‌پی به همان ابزار پرداخت خریدار برمی‌گردد.'}
                      {' '}هیچ مبلغی به کیف پول کازماشاپ واریز نمی‌شود و <strong>این عملیات برگشت‌ناپذیر است.</strong>
                    </div>
                  )}
                  {isZarinpalOrder(detail) && (
                    <div style={{ padding: 12, background: 'var(--color-info-bg)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>مقصد استرداد</div>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, cursor: 'pointer' }}>
                        <input type="radio" name="refund-item-target" checked={refundItemTarget === 'gateway'} disabled={!zpConfig?.refundEnabled} onChange={() => setRefundItemTarget('gateway')} />
                        <span>برگشت به کارت خریدار از زرین‌پال (استرداد جزئی){!zpConfig?.refundEnabled && ' — غیرفعال (توکن پنل تنظیم نشده)'}</span>
                      </label>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" name="refund-item-target" checked={refundItemTarget === 'wallet'} onChange={() => setRefundItemTarget('wallet')} />
                        <span>واریز به کیف پول کاربر در سایت</span>
                      </label>
                      {refundItemTarget === 'gateway' && (
                        <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                          <span>روش واریز:</span>
                          <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value as 'CARD' | 'PAYA')} style={{ padding: '4px 8px' }}>
                            <option value="CARD">آنی به کارت (CARD)</option>
                            <option value="PAYA">پایا — سیکل بعدی (PAYA)</option>
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                  <SurchargeRefundSwitch order={detail} />
                  <RefundPreviewSteps preview={itemRefundPreview} loading={itemRefundPreviewLoading} error={itemRefundPreviewError} />
                  <div style={{ padding: 12, background: 'var(--color-bg-default)', borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{selectedItem.productTitle}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span>قیمت محصول:</span>
                      <strong>{formatPrice(selectedItem.totalPrice)} تومان</strong>
                    </div>
                    {selectedItem.fulfillmentType === 'postal_delivery' && selectedItem.postalDeliveryDetails?.shippingCost && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                        <span>هزینه ارسال:</span>
                        <strong>{formatPrice(selectedItem.postalDeliveryDetails.shippingCost * (selectedItem.quantity || 1))} تومان</strong>
                      </div>
                    )}
                    <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: 'var(--color-success-text, #28a745)' }}>
                      <span>مبلغ بازپرداخت:</span>
                      <strong>
                        {formatPrice(
                          selectedItem.totalPrice +
                          (selectedItem.fulfillmentType === 'postal_delivery' ? (selectedItem.postalDeliveryDetails?.shippingCost || 0) * (selectedItem.quantity || 1) : 0)
                        )} تومان
                      </strong>
                    </div>
                  </div>
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>دلیل بازپرداخت</label>
                      <textarea
                        rows={2}
                        value={refundItemReason}
                        onChange={(e) => setRefundItemReason(e.target.value)}
                        placeholder="مثال: محصول آسیب‌دیده رسید..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => { setRefundItemDialogOpen(false); setSelectedItem(null); }}>انصراف</button>
          <button className="edit-btn submit" onClick={handleRefundItem} disabled={refundItemLoading}>
            {refundItemLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {refundItemLoading ? 'در حال پردازش...' : 'بازپرداخت'}
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default OrderManagement;
